/*==============================================================================
#TABLE OF CONTENTS

1)    #INITIALIZE
2)    #RESET BUILDER
3)    #CLEAR STEP
4)    #BUILD STEP
5)    #BUILD PROGRESS
6)    #BUILD STEP CONTENT
7)    #BUILD CONTROLS
8)    #CHANGE STEP
9)    #BUILDER SELECTION
10)   #LOAD PRODUCT
11)   #CONFIGURE STEP
12)   #SELECT CALLBACK
13)   #TRIGGER INIT
==============================================================================*/


class CustomBuilder {
    // Builder constructor
    constructor(builderType, jsonSrc, discountSrc, baseProductId) {
        const validTypes = ['bike', 'wheel'];

        if (!validTypes.includes(builderType)) {
            throw new Error(`Invalid builder type: ${builderType}`);
        }

        this.builderType = builderType;
        this.jsonSrc = jsonSrc;
        this.discountSrc = discountSrc;
        this.baseProductId = baseProductId;
        this.json = {};
        this.currentStep = 0;
        this.currentStepJson = {};
        this.currentStepRequired = true;
        this.currentCollectionUrl = '';
        this.builderProductsUrl = '?section_id=builder-products';
        this.abortController = null;
        this.subcategory = '';
        this.selections = [];
        this.discounts = {};
        this.stepVariantDependency = [];
        this.assetUrl = "//cdn.shopify.com/s/files/1/0904/4132/files/?v=1683244810".split('?');
        this.variantFilterObserver = null;
        this.weight = 0;
        this.cost = 0;

        // Selectors
        this.currentStepTitle = '';
        this.productDetailsContent = document.getElementById('builder-product-details');
        this.productPopover = document.getElementById('builder-product-popover');
        this.firstStepContent = document.getElementById('builder-first-step');
        this.finalStepContent = document.getElementById('builder-final-step');
        this.completeStepContent = document.getElementById('builder-complete-step');
        this.controls = document.getElementById('builder-controls');
        this.displayWeight = document.getElementById('build-weight');
        this.displayCost = document.getElementById('build-cost');
        this.selectionContainer = document.querySelector('#builder-selection-container');
        this.selectionHeader = document.querySelector('#builder-selection-header h2');
        this.selectionContent = document.getElementById('builder-selection-content');
        this.weightInput = document.getElementById('user-weight');
        this.heightInput = document.getElementById('user-height');
        this.saddleInput = document.getElementById('user-saddle');
        this.shirtInput = document.getElementById('user-tshirt');
        this.infoInput = document.getElementById('additional-info');
        this.completeButton = document.getElementById('complete-build-button');
        this.skipButton = document.getElementById('skip-button');
        this.summaryDrawer = document.getElementById('build-summary-drawer');
        
        // Error Messages
        this.weightErrorMessage = 'Enter rider weight in pounds.';
        this.heightErrorMessage = 'Enter height in inches.';
        this.saddleErrorMessage = 'Enter saddle height in millimeters.';

        // Bikologi properties
        this.canvas;
        this.imgStore;
        this.bikologiBikeImageUrl = 'https://bikologi-stage-images.herokuapp.com/trail-bike.png';
        this.bikologiWheelImageUrl = 'https://bikologi-stage-images.herokuapp.com/wheels.png';
        this.customPickerInit = false;
        this.pickerColor = '';
        this.spokePattern = {};
        this.activeBindings = {};
        this.activeBuild = {};
        this.existingBuild = {};
        this.components = {};
        this.bikes = {};

        // Initialize the bike builder
        this.init();
    }

    // Initiatlize builder
    async init() {
        console.log('Initialize Custom Builder');
        console.log('BaseProductId: ', this.baseProductId);

        try {
            this.json = await this.fetchJSON(this.jsonSrc);
        } catch (error) {
            console.error(error.message);
        }

        try {
            this.discounts = await this.fetchJSON(this.discountSrc);
        } catch (error) {
            console.error(error.message);
        }

        console.log('DISCOUNTS:', this.discounts);

        this.buildStep(this.currentStep);
        this.setupEventListeners();
    }

    // Fetch JSON config files
    async fetchJSON(url) {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Error fetching JSON from ${url}`);
        return response.json();
    }

    // Set up event listeners
    setupEventListeners() {
        console.log('----------------------------------------------------------');
        console.log('Setup Event Listeners');

        const previousButton = document.getElementById('previous-button');
        if (previousButton) {
            previousButton.addEventListener('click', () => this.previousStep());
        }

        const skipButton = document.getElementById('skip-button');
        if (skipButton) {
            skipButton.addEventListener('click', () => this.skipStep());
        }

        const restartButton = document.getElementById('restart-button');
        if (restartButton) {
            restartButton.addEventListener('click', () => {

                let confirmRestart = confirm("Are you sure you want to restart your build?");

                if (confirmRestart) {
                    this.restartBuilder()
                }
            });
        }

        const buildSummaryButton = document.getElementById('build-summary-button');
        if (buildSummaryButton) {
            buildSummaryButton.addEventListener('click', () => this.buildSummary());
        }

        const completeBuildButton = document.getElementById('complete-build-button');
        if (completeBuildButton) {
            completeBuildButton.addEventListener('click', () => this.completeBuild());
        }

        // Add event listeners for each input field to check on input
        if (this.builderType === 'bike') {
            const inputs = [
                { el: this.weightInput, name: 'Weight', message: this.weightErrorMessage },
                { el: this.heightInput, name: 'Height', message: this.heightErrorMessage },
                { el: this.saddleInput, name: 'Saddle', message: this.saddleErrorMessage }
            ];

            inputs.forEach(({ el, message }) => {
                el.addEventListener('input', () => {
                    el.dataset.dirty = 'true';
                    this.validateInputs();
                });

                el.addEventListener('blur', () => {
                    el.dataset.dirty = 'true';
                    this.toggleValidationMessage(el, CustomBuilder.isValidNumeric(CustomBuilder.sanitizeInput(el.value)), message);
                });
            });
        }

        this.productPopover.addEventListener('openable-element:open', () => {
            console.log('POPOVER OPEN')
        });

        this.productPopover.addEventListener('openable-element:close', () => {
            console.log('POPOVER CLOSE')
        });

        // Setup event listener for variant changes
        const builder = document.getElementById('builder');
        builder.addEventListener('variant:changed', (event) => {
            const isMobile = window.matchMedia('(max-width:768px)').matches;
            const activeProductDetails = isMobile
                ? document.getElementById('builder-product-details-popover')
                : document.getElementById('builder-product-details');

            if (activeProductDetails.contains(event.target)) {
                this.handleVariantChange(event);
            }
        });

        // Setup event listener for back button
        this.protectAgainstBackNavigation(
            'Whoa there!  If you go back, all your work in the builder will be lost. Continue?'
        );
    }

    // Intercepts back button event and prompts user to confirm they want to go back
    protectAgainstBackNavigation(message = 'If you go back you’ll lose your build.  Leave anyway?') {
        // Tag the *current* entry as our “builder” entry
        history.replaceState({ _building: true }, '');

        // Push one more entry on top (neutral state)
        history.pushState(null, '');

        // Listen for popstate
        this._onPopState = (evt) => {
            // If we land on our “builder” entry, that's a back from the neutral state
            if (evt.state && evt.state._building) {
                if (confirm(message)) {
                    // User really wants to leave: clean up and go back one more
                    window.removeEventListener('popstate', this._onPopState); -
                        history.back();
                } else {
                    // User canceled: push back up to the neutral state
                    history.pushState(null, '');
                }
            }
            // NOTE: if evt.state===null (the neutral state), we do nothing and let
            // the browser handle further back navigation (which takes them away).
        };

        window.addEventListener('popstate', this._onPopState);
    }


    // Changes step
    nextStep() {
        console.log('----------------------------------------------------------');
        console.log('Next Step');

        this.currentStep += 1;
        this.buildStep(this.currentStep);
    }

    // Moves to the previous step
    previousStep() {
        console.log('----------------------------------------------------------');
        console.log('Previous Step');

        if (this.currentStep === 1) {
            // Going back to first step so clear canvas
            this.clearCanvas();
            this.currentStep = 0;
        } else {
            // Remove current item from active build
            this.removeCurrentItemFromCanvas();

            // Remove any trailing null array items
            while (this.selections.length && this.selections[this.selections.length - 1] == null) {
                this.selections.pop();
            }

            // Remove the last selected item and set currentStep and subcategory
            const lastSelection = this.selections.pop();
            this.currentStep = lastSelection.step_index || this.currentStep - 1;
            this.subcategory = lastSelection.subcategory || '';
        }

        // Go back to the previous selection's step
        this.buildStep(this.currentStep);
    }

    // Skips optional step
    skipStep() {
        console.log('----------------------------------------------------------');
        console.log(`Skip Step`);

        // Remove current item from active build
        this.removeCurrentItemFromCanvas();

        this.handleSelection('skip-step', 'collection')
    }

    // Builds current step
    buildStep(stepIndex) {
        console.log('----------------------------------------------------------');
        console.log(`Building step ${stepIndex}`);

        // Clear out any existing html
        this.productDetailsContent.innerHTML = '';
        this.selectionContent.innerHTML = '';

        // Update current step properties on builder objecdt
        this.currentStepJson = this.json[stepIndex];
        this.currentStepTitle = this.currentStepJson.title;
        this.currentStepRequired = this.currentStepJson.required;

        // Display the correct controls and builder details panel
        this.toggleControlsAndDetails(stepIndex);

        // Display builder selection content for current step
        this.displaySelectors(this.currentStep);
    }

    // Displays selection content (Products, radio buttons, text forms, etc)
    async displaySelectors(stepIndex) {
        console.log('----------------------------------------------------------');
        console.log(`Display Selectors for step ${stepIndex}`);
        console.log('----------------------------------------------------------');

        // Show step loading spinner
        this.showSpinner();

        // Get current step JSON from builder object
        let currentStepJson = this.currentStepJson;

        // Check for subcategory (Ex: prebuilt or custom built wheels)
        if (currentStepJson.tagged && currentStepJson.tagged !== this.subcategory) {
            // Current step is not the correct subcategory so skip step
            this.nextStep();
            return;
        } else if (!currentStepJson.tagged && this.subcategory !== '') {
            // No longer in subcategory so clear subcategory
            this.subcategory = '';
        }

        const dependencies = currentStepJson.dependencies;
        this.stepVariantDependency = [];

        switch (currentStepJson.type) {

            case 'radio':
                console.log('Display Radio Buttons');

                if (dependencies) {
                    this.selections.forEach((selection) => {
                        if (!selection) return;

                        console.log('Selection:', selection);

                        switch (selection.type) {
                            case 'collection':
                                dependencies.forEach((dependency) => {
                                    if (dependency === selection.handle) {
                                        if (selection.product_title !== '') {
                                            if (selection.variant_dependency) {
                                                let variantDependencies = selection.variant_dependency.includes(',')
                                                    ? selection.variant_dependency.split(',')
                                                    : [selection.variant_dependency];

                                                variantDependencies.forEach((v_dependency) => {
                                                    let [current_dependency, variant] = v_dependency.split('_');

                                                    if (current_dependency.includes('[v]')) {
                                                        current_dependency = current_dependency.replace('[v]', '');
                                                        if (current_dependency === currentStepJson.handle) {
                                                            this.stepVariantDependency.push(CustomBuilder.handleize(variant));
                                                        }
                                                    }
                                                });
                                            }
                                        }
                                    }
                                });

                                break;
                        }
                    });
                }


                let radioList = document.createElement('ul');
                radioList.classList.add('bike-step-radio-list', currentStepJson.handle);
                this.selectionContent.appendChild(radioList);

                this.createRadios(currentStepJson, radioList);

                // Select all radio buttons inside the step content container
                const radioButtons = document.querySelectorAll('#builder-selection-content input[type="radio"]');

                radioButtons.forEach((radio) => {
                    radio.addEventListener("change", () => {
                        this.handleSelection(radio, 'radio');
                    });
                });
                break;

            case 'collection':
                console.log('Display Product Collection');

                let tags = [];

                if (dependencies) {
                    this.selections.forEach((selection) => {
                        if (!selection) return;

                        switch (selection.type) {
                            case 'radio':
                                dependencies.forEach((dependency) => {
                                    if (dependency === selection.handle) {
                                        tags.push(selection.value);
                                    }
                                });
                                break;

                            case 'collection':
                                dependencies.forEach((dependency) => {
                                    if (dependency === selection.handle) {
                                        if (selection.product_title !== '') {
                                            tags.push(selection.product_title);

                                            if (selection.variant_dependency) {
                                                let variantDependencies = selection.variant_dependency.includes(',')
                                                    ? selection.variant_dependency.split(',')
                                                    : [selection.variant_dependency];

                                                variantDependencies.forEach((v_dependency) => {
                                                    let [current_dependency, variant] = v_dependency.split('_');

                                                    if (current_dependency.includes('[v]')) {
                                                        current_dependency = current_dependency.replace('[v]', '');
                                                        if (current_dependency === currentStepJson.handle) {
                                                            this.stepVariantDependency.push(CustomBuilder.handleize(variant));
                                                        }
                                                    } else if (current_dependency === currentStepJson.handle) {
                                                        tags.push(v_dependency);
                                                    }
                                                });
                                            }
                                        }
                                    }
                                });

                                break;
                        }
                    });

                    // No tags found for dependency, assume we need to skip step with 'no-tags' filter
                    if (tags.length === 0) {
                        tags.push('no-tags');
                    }
                }

                // Sort tags before building the collection URL.
                // Shopify canonicalizes collection tag URLs alphabetically, so sorting
                // avoids unnecessary redirects for valid tag combinations.
                tags = tags.sort().join('+');
                let collection_url = '/collections/' + currentStepJson.collection + '/' + tags + this.builderProductsUrl;
                this.currentCollectionUrl = collection_url;
                
                
                const collectionLoaded = await this.loadCollection(collection_url);
                if(!collectionLoaded) {
                    return;
                }

                break;

            case 'textarea':
                console.log('Display Text Area');
                break;

            case 'form-group':
                console.log('Display Form Group');
                break;
        }

        // hide step loading spinner
        this.hideSpinner();
    }

    // Loads collection using Shopify Section Rendering API
    // Returns false and skips the step if no matching products are found
    async loadCollection(sectionUrl) {
        const builderSelectionContent = document.getElementById('builder-selection-content');

        try {
            const response = await fetch(sectionUrl);
            const html = await response.text();

            // Shopify may canonicalize collection tag URLs by reordering tags.
            // If a followed redirect changes or removes the requested tag set,
            // treat it as no matching products and skip the step.
            const getTags = (url) => {
                const parts = new URL(url, window.location.origin)
                    .pathname
                    .split('/')
                    .filter(Boolean);

                return parts[2] ? parts[2].split('+').sort() : [];
            };

            const requestedTags = getTags(sectionUrl);
            const finalTags = getTags(response.url);

            const tagsChanged =
                requestedTags.length !== finalTags.length ||
                requestedTags.some((tag, index) => tag !== finalTags[index]);

            // Shopify theme dev may return a raw 3xx response instead of following
            // the redirect, while production fetches may follow it automatically.
            const isRedirectResponse =
                response.status >= 300 &&
                response.status < 400;

            // Skip the step when Shopify redirects to a different tag set,
            // returns a raw redirect/empty response, or explicitly renders no products.
            const hasNoMatchingProducts =
                (response.redirected && tagsChanged) ||
                isRedirectResponse ||
                html.trim() === '' ||
                html.includes('No products found in this category.');

            if (hasNoMatchingProducts) {
                console.log('No matching products. Skipping step.', {
                    status: response.status,
                    redirected: response.redirected,
                    requestedUrl: sectionUrl,
                    finalUrl: response.url,
                    ...(tagsChanged && {
                        requestedTags,
                        finalTags
                    })
                });

                this.nextStep();
                return false;
            }

            builderSelectionContent.innerHTML = html;

            // Load Product Details for first product if step is required
            this.selectFirstProduct(builderSelectionContent);

            // Re-bind filter event listener
            const facetWrapper = builderSelectionContent.querySelector('facet-filters');
            if (facetWrapper) {
                facetWrapper.addEventListener('facet:criteria-changed', (event) => {
                    event.stopImmediatePropagation();
                    event.preventDefault?.();
                    const url = new URL(event.detail.url, window.location.origin);
                    const queryString = url.search; // includes the "?"
                    this.reloadFilteredCollection(queryString);
                });
            }

            // Re-bind product click events
            this.bindProductClicks(builderSelectionContent);

            return true;

        } catch (error) {
            console.error("Error loading collection:", error);
            return false;
        }
    }

    // Selects the first product after loading a product collection
    // If step is not required it doesnt' load a product and enables the skip button instead
    async selectFirstProduct(container) {
        if (this.currentStepJson.required) {
            const firstProduct = container.querySelector('product-item');
            const firstProductUrl = firstProduct?.querySelector('a')?.href;
            if (firstProductUrl) {
                await this.loadProductDetails(firstProductUrl);
            }
        } else {
            // Enable skip button if step is optional
            this.skipButton.disabled = false;
        }
    }

    // Finds all products in collection list and binds events to prevent them from opening product page
    // Instead clicks will load product details and if mobile will open the popover
    bindProductClicks(container) {
        const isMobile = window.matchMedia('(max-width: 768px)').matches;
        container.querySelectorAll('.builder-product-item-anchor').forEach(productItemAnchor => {
            productItemAnchor.addEventListener('click', async (event) => {
                event.preventDefault();
                event.stopImmediatePropagation();
                await this.loadProductDetails(productItemAnchor.href);
                if (isMobile) {
                    this.productPopover.open = true;
                }
            }, true);
        });
    }

    // Reloads product collection when filters are applied
    async reloadFilteredCollection(queryString) {
        // Create new url with filters applied
        const filteredUrl = `${this.currentCollectionUrl}${queryString ? '&' + queryString.slice(1) : ''}`;

        // Abort any previous fetch
        if (this.abortController) {
            this.abortController.abort();
        }

        this.abortController = new AbortController();

        try {
            const response = await fetch(filteredUrl, { signal: this.abortController.signal });
            const sectionHtml = await response.text();
            const fakeDiv = document.createElement("div");
            fakeDiv.innerHTML = sectionHtml;

            // Replace only the facet-main area
            const newFacetMain = fakeDiv.querySelector("#facet-main");
            if (newFacetMain) {
                const facetMain = document.querySelector("#facet-main");
                facetMain.innerHTML = newFacetMain.innerHTML;
                this.bindProductClicks(facetMain);
                this.selectFirstProduct(facetMain);
            }

            // Preserve facet drawer content
            const newFilters = fakeDiv.querySelector("#facet-filters");
            if (newFilters) {
                const currentFilters = document.querySelector("#facet-filters");

                // Copy expanded/collapsible state before replace
                currentFilters.querySelectorAll(".collapsible-toggle").forEach((toggle) => {
                    const id = toggle.getAttribute("aria-controls");
                    const isExpanded = toggle.getAttribute("aria-expanded") === "true";
                    const matchingNewToggle = newFilters.querySelector(`[aria-controls="${id}"]`);

                    if (matchingNewToggle) {
                        matchingNewToggle.setAttribute("aria-expanded", isExpanded ? "true" : "false");
                        const content = matchingNewToggle.nextElementSibling;
                        if (content) {
                            content.toggleAttribute("open", isExpanded);
                            content.style.overflow = isExpanded ? "visible" : "";
                        }
                    }
                });

                // Store current scroll position
                const drawerContent = currentFilters.querySelector('.drawer__content');
                const previousScrollTop = drawerContent?.scrollTop || 0;

                // Replace filters
                currentFilters.innerHTML = newFilters.innerHTML;

                // Restore scroll position after replacing content
                const newDrawerContent = currentFilters.querySelector('.drawer__content');
                if (newDrawerContent) {
                    newDrawerContent.scrollTo(0, previousScrollTop);
                }

            }
        } catch (error) {
            if (error.name === 'AbortError') {
                console.log("Fetch aborted");
                return;
            }
            console.error("Error reloading collection", error, "URL:", filteredUrl);

        }
    }

    // Hides step title and shows spinner
    showSpinner() {
        document.getElementById('step-title-text')?.classList.add('hidden');
        document.getElementById('step-loading-spinner')?.classList.remove('hidden');
    }

    // Shows step title and hides spinner
    hideSpinner() {
        document.getElementById('step-loading-spinner')?.classList.add('hidden');
        let stepTitleText =  document.getElementById('step-title-text');

        // Show step title unless it is the final step
        if (this.currentStepTitle !== 'Final Details') {
            stepTitleText.classList.remove('hidden');
            stepTitleText.innerText = this.currentStepRequired
                ? this.currentStepJson.title
                : this.currentStepJson.title + ' (Optional)';
        }
    }

    // Callback to handle user selection and add to build
    handleSelection(element, type) {
        console.log('----------------------------------------------------------');
        console.log('Handle Selection');

        switch (type) {
            case 'radio':
                this.selections[this.currentStep] = {
                    'handle': this.currentStepJson.handle,
                    'step_index': this.currentStep,
                    'step_title': this.currentStepJson.title,
                    'short_title': this.currentStepJson.short_title,
                    'value': element.value,
                    'product_id': element.getAttribute('data-variant-id'),
                    'selection': element.getAttribute('data-title'),
                    'type': this.currentStepJson.type,
                    'price': element.getAttribute('data-price'),
                    'weight': element.getAttribute('data-weight'),
                    'subcategory': this.subcategory
                };

                // Set subcategory if it exists
                this.subcategory = element.getAttribute('data-show-tagged') || this.subcategory;

                break;

            case 'collection':
                if (element === 'skip-step') {
                    this.selections[this.currentStep] = {
                        'handle': this.currentStepJson.handle,
                        'step_index': this.currentStep,
                        'step_title': this.currentStepJson.title,
                        'short_title': this.currentStepJson.short_title,
                        'value': '',
                        'product_id': 'skip-step',
                        'product_title': 'skip',
                        'selection': 'Skipped Step',
                        'variant_title': 0,
                        'weight': 0,
                        'price': 0,
                        'type': this.currentStepJson.type,
                        'subcategory': this.subcategory
                    }
                } else {

                    // Select the hidden input field in form element passed into function using its name attribute
                    const productIdInput = element.querySelector('input[name="product-id"]');
                    let product_id = productIdInput ? productIdInput.value : null;

                    // Get JSON variant data from product form located in product-form.liquid variant picker
                    const variantScript = element.querySelector("script[data-variant]");
                    let variant_id;
                    let product_title;
                    let variant_title;
                    let variant_price;
                    let variant_weight;
                    let variant_dependency;
                    let selection;

                    if (variantScript) {
                        const variant = JSON.parse(variantScript.textContent);
                        variant_id = variant.id;
                        product_title = variant.builder_data.product_title_handle;
                        variant_title = variant.builder_data.variant_title_handle;
                        variant_price = variant.price;
                        variant_weight = variant.builder_data.product_weight_grams || 0;
                        variant_dependency = variant.builder_data.variant_builder_dependency || "";
                        selection = variant.name;

                    } else {
                        console.error("[BUILDER] builderSelection: No variant data found in the form element.");
                    }

                    this.selections[this.currentStep] = {
                        'handle': this.currentStepJson.handle,
                        'step_index': this.currentStep,
                        'step_title': this.currentStepJson.title,
                        'short_title': this.currentStepJson.short_title,
                        'value': variant_id,
                        'product_id': product_id,
                        'product_title': product_title,
                        'selection': selection,
                        'variant_title': variant_title,
                        'variant_id': variant_id,
                        'weight': variant_weight,
                        'price': parseFloat(variant_price),
                        'type': this.currentStepJson.type,
                        'variant_dependency': variant_dependency,
                        'subcategory': this.subcategory
                    }

                }

                break;

        }

        // Finished handling selection so proceed to next step
        this.calculateTotals();
        this.cleanUpVariantListeners();
        this.nextStep();

        const isMobile = window.matchMedia('(max-width: 768px)').matches;

        // grab the height of your fixed header
        const announcementBar = document.querySelector('.shopify-section--announcement-bar');
        const header = document.querySelector('.shopify-section--header');
        const offset = isMobile ? (header ? header.offsetHeight + announcementBar.offsetHeight : 108) : 0;

        // then scroll past it
        window.scrollTo({
            top: offset,
            behavior: 'smooth'
        });

    }

    // Creates radio buttons for step
    createRadios(currentStepJson, element, additional_name = '') {
        // Recursive function that creates all radio buttons and their children
        console.log('----------------------------------------------------------');
        console.log('Create Radios');

        currentStepJson.options.forEach((value) => {
            // Check if this radio is in variant dependcies and skip if not
            if (this.stepVariantDependency.length !== 0 && !this.stepVariantDependency.includes(value.handle)) {
                return;
            }
            let li = document.createElement('li');
            li.classList.add(value.handle);
            li.classList.add(value.image ? 'radio-image' : 'radio');
            li.setAttribute('data-dep', value.handle);

            let input = document.createElement('input');
            input.type = 'radio';
            input.name = currentStepJson.handle + '-' + additional_name;
            input.value = value.handle;
            input.id = value.handle + '-' + additional_name;
            if (value.show_tagged) input.setAttribute('data-show-tagged', value.show_tagged);
            input.setAttribute('data-title', value.title);

            let label = document.createElement('label');
            label.setAttribute('for', value.handle + '-' + additional_name);
            if (value.image) {
                label.style.backgroundImage = `url(${this.assetUrl[0]}${value.image}?${this.assetUrl[1]})`;
            }
            label.ariaLabel = value.title;

            li.appendChild(input);
            li.appendChild(label);

            // Add nested radio buttons if available
            if (value.options) {
                let nestedRadioList = document.createElement('ul');
                nestedRadioList.classList.add('nested-bike-radio-list-' + value.handle);
                nestedRadioList.style.display = 'none';
                li.appendChild(nestedRadioList);
                createRadios(value.options, nestedRadioList, value.handle, currentStepJson);
            }

            element.appendChild(li);
        });
    }

    // Loads selected product details panel
    async loadProductDetails(productUrl) {
        console.log('----------------------------------------------------------');
        console.log("Load Product Details");
        console.log('----------------------------------------------------------');

        // Get the correct product details container for mobile or desktop
        const isMobile = window.matchMedia('(max-width: 768px)').matches;
        const productDetailsId = isMobile
            ? 'builder-product-details-popover'
            : 'builder-product-details';

        const productDetails = document.getElementById(productDetailsId);
        const builderDetailsContainer = document.getElementById('builder-right');

        if (!productDetails) {
            console.warn('[BUILDER] productDetails not found');
            return;
        }

        // Reset all listeners and mutation observers
        this.cleanUpVariantListeners();

        // Remove highlight
        builderDetailsContainer.classList.remove('highlight');

        // Highlight to get users attention after delay        
        setTimeout(() => {
            builderDetailsContainer.classList.add('highlight');
        }, 200);

        // Build URL for section rendering api
        const sectionUrl = new URL(productUrl);
        sectionUrl.searchParams.set('section_id', 'builder-product-details');

        console.log('Section URL:', sectionUrl);

        // Use Shopify Section Render API to render builder products
        try {
            const response = await fetch(sectionUrl.toString());
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const html = await response.text();
            productDetails.innerHTML = html;

            // Add event listener for form submission so we can capture add to cart event
            productDetails.addEventListener('submit', (event) => {
                if (event.target.matches('form')) {
                    console.log('Product Details Form Submitted!!!');
                    event.preventDefault(); // Prevent form submission
                    event.stopImmediatePropagation(); // Stop other listeners from submitting

                    // Get the selected variant ID from the hidden input field
                    let selectedVariantId = productDetails.querySelector('input[name="id"]').value;
                    console.log("Selected Variant ID:", selectedVariantId);

                    // Get the variant data from the shopify-accelerated-checkout element
                    let variantDataElement = document.querySelector('[variant-params]');
                    if (variantDataElement) {
                        let variantData = JSON.parse(variantDataElement.getAttribute('variant-params'));

                        // Find the selected variant details
                        let selectedVariant = variantData.find(variant => variant.id.toString() === selectedVariantId);
                        console.log("Selected Variant Data:", selectedVariant);
                    }

                    // Call handleSelection to begin canvas drawing
                    this.handleSelection(productDetails, 'collection');

                    // Close popover if on mobile
                    if (isMobile) {
                        this.productPopover.open = false;
                    }
                }
            }, true); // Capture phase to override other event listeners

        } catch (error) {
            console.error("Error loading product details:", error);
        } finally {

            // Obtain product id and Bikologi product metafields
            const productForm = productDetails.querySelector('.builder-product-form');

            if (productForm) {

                const bikologiData = productForm?.dataset?.bikologi;
                let productData;

                if (bikologiData) {
                    try {
                        productData = JSON.parse(bikologiData);

                        // Populate Bikologi with bindings
                        if (productData.id && productData.metafields?.bikologi) {
                            this.activeBindings[productData.id] = productData.metafields.bikologi;
                        } else {
                            console.warn('[Bikologi] No productData.id or metafields.bikologi found:', productData);
                        }

                    } catch (err) {
                        console.error('Error parsing bikologi data:', err);
                    }
                } else {
                    console.warn('No bikologi data found on productForm!');
                }

                // This code filters out variants but doesn't work because when a new variant is selected, 
                // the theme re-renders the variant picker. Going to try server side, this can be deleted later
                const blockSwatchList = productForm.querySelector('.block-swatch-list');
                if (this.stepVariantDependency.length > 0 && blockSwatchList) {
                    this.setupVariantFilterObserver()
                    await this.filterVariants(true);
                } else {
                    // Only start Bikologi canvas process if we don't have to filter variants
                    // If variants are being filtered, it will get called after that
                    // Prevents multiple calls to paint canvas
                    this.handleProductBuild(productData);
                }

            } else {
                console.warn('No productForm found inside builderProductDetails!');
            }

        }
    }

    // Finalizes build and adds to cart
    async completeBuild() {
        console.log('----------------------------------------------------------');
        console.log("Complete Build...");
        console.log('Here are the items we need to add: ');
        console.log(this.selections);

        // Set up product cart properties
        let builderId = (new Date().getTime() / 1000).toFixed(0);
        let discount = this.calculateDiscount();
        let itemsToAdd = [];

        let builderCartProperties = {
            '_custom_builder_id': builderId,
            '_master_custom_builder': '1',
            '_bikebuilder': '1'
        };

        if (discount) {
            // builderCartProperties['_discount'] = discount;
            builderCartProperties._discount = discount;
        }

        // Add weight to properties
        // builderCartProperties.push('properties[_estimated build weight]='+ this.formatWeight(this.weight));
        builderCartProperties._estimated_build_weight = CustomBuilder.formatWeight(this.weight);

        // Collect user data from final form
        if(this.builderType === 'bike') {
            if (this.weightInput) {
                builderCartProperties._customer_weight = CustomBuilder.sanitizeInput(this.weightInput.value);
            }
            if (this.heightInput) {
                builderCartProperties._customer_height = CustomBuilder.sanitizeInput(this.heightInput.value);
            }
            if (this.saddleInput) {
                builderCartProperties._saddle_height = CustomBuilder.sanitizeInput(this.saddleInput.value);
            }
            if (this.shirtInput) {
                builderCartProperties._shirt_size = this.shirtInput.value;
            }
        } else {
            if (this.infoInput) {
                builderCartProperties._customer_info = CustomBuilder.sanitizeTextAreaInput(this.infoInput.value);
            }
        }

        // Add bikologi image URL to properties
        let buildImageUrl = document.getElementById("builder-image").getAttribute("data-src");
        builderCartProperties._build_image_url = buildImageUrl;

        // Not sure if we are going to use this
        // var analytics_build_selections = [];

        // Loop through items and build out JSON to add to cart        
        this.selections.forEach(item => {
            if (item) {
                if (item.type === 'radio' || item.type === 'textarea') {
                    builderCartProperties['_' + item.handle] = item.value;
                } else if (item.type === 'form-group') {
                    // TODO: Need to fix this
                    // item.value.forEach(value => {
                    //     builderCartProperties.push('properties[_' + value.replace('=', ']='));
                    //     builderCartProperties['_' + value.replace('=', ']=')];
                    // });
                } else if (item.type === 'collection') {
                    if (item.product_id === 'skip-step') {
                        builderCartProperties['_' + item.handle] = 'none';
                    } else {
                        builderCartProperties['_' + item.handle] = item.product_title + ' - ' + item.variant_title;
                    }

                    if (item.variant_id) {

                        // Set child properties of variant
                        let variantProperties = discount
                            ? { _custom_builder_id: builderId, _discount: discount, _component_title: item.short_title }
                            : { _custom_builder_id: builderId, _component_title: item.short_title };

                        itemsToAdd.push(
                            {
                                id: item.variant_id,
                                quantity: 1,
                                properties: variantProperties
                            });

                        // analytics_build_selections.push({
                        //   builder_step: value.step_title,
                        //   product_title: value.selection,
                        //   product_id: value.product_id,
                        //   variant_id: value.variant_id
                        // });

                    }
                }
            }
        });

        console.log('Builder Cart Properties: ', builderCartProperties);

        // **Ensure there's something to add**
        if (itemsToAdd.length === 0) {
            console.warn("No items to add to cart.");
            return;
        }

        // Add base builder product to array
        let baseProductData =
        {
            id: this.baseProductId,
            quantity: 1,
            properties: builderCartProperties
        };

        // Reverse the items so they display in the cart in order of builder selections
        itemsToAdd = itemsToAdd.reverse();

        const finalItemsToAdd = [baseProductData, ...itemsToAdd];
        console.log('Processed Items to Add: ', finalItemsToAdd);

        // Build request payload
        const formData = { items: finalItemsToAdd };
        console.log('Final payload:', JSON.stringify(formData, null, 2));

        try {
            const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify(formData),
            });

            const payload = await response.json();
            if (!response.ok) {
                throw new Error(payload.description || 'Failed to add items to cart');
            }

            console.log('Cart updated successfully:', payload);

            // Display completed build panel
            this.loadConfirmationPanel();

            // Refresh the mini-cart
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

            // Open the mini cart
            document.getElementById('mini-cart').open = true;

        } catch (error) {
            console.error('Error adding items to cart:', error);
            // TODOD: Show user facing error here
        }

        // // Add the items to the cart
        // fetch(window.Shopify.routes.root + 'cart/add.js', {
        //     method: 'POST',
        //     headers: { 'Content-Type': 'application/json' },
        //     body: JSON.stringify(formData)
        // })
        //     .then(response => response.json())
        //     .then(data => {
        //         console.log("Cart updated successfully:", data);
        //         // console.log("Cart updated successfully:", JSON.stringify(data, null, 2));
        //         // TODO: Update UI here
        //         // TODO: Need to figure out why console is giving an error in handleFetchRequest

        //         // Display completed build
        //         this.loadConfirmationPanel();

        //         // Refresh the cart
        //         document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        //             bubbles: true
        //         }));

        //         // Open the mini cart
        //         document.getElementById('mini-cart').open = true;
        //     })
        //     .catch(error => {
        //         console.error('Error adding items to cart:', error);
        //     });

    }

    // Calculates builder discount and returns discount code
    calculateDiscount() {
        console.log('----------------------------------------------------------');
        console.log("Calculate Discount...");

        const brand = this.selections[0].selection;
        const brandDiscounts = this.discounts[brand];
        const totalBuildCost = parseFloat(this.cost / 100).toFixed(2);
        let discountCode;
        let highestDiscount = 0;

        console.log('Brand:', brand);
        console.log('Brand Discounts:', brandDiscounts);
        console.log('Build Cost:', totalBuildCost);

        // Loop through discounts and choose the highteset discount
        if (brandDiscounts) {
            brandDiscounts.forEach(discount => {
                if (discount.amount < totalBuildCost && discount.amount > highestDiscount) {
                    highestDiscount = discount.amount;
                    discountCode = discount.code;
                }
            });
        } else {
            console.warn(`No discounts found for brand: ${brand}`);
        }

        console.log('Discount code:', discountCode);
        return discountCode;
    }

    // Calculates total weight and cost
    calculateTotals() {
        console.log('----------------------------------------------------------');
        console.log("Calculate Totals...");

        let totalWeight = 0;
        let totalCost = 0;

        // Total up weight and cost
        this.selections.forEach(selection => {
            totalWeight += parseFloat(selection.weight) || 0;
            totalCost += parseFloat(selection.price) || 0;
        });

        console.log('Total Weight:', totalWeight);
        console.log('Total Cost:', totalCost);

        this.weight = totalWeight;
        this.cost = totalCost;

        this.updateTotals();
    }

    // Updates Build Totals
    updateTotals() {
        console.log('----------------------------------------------------------');
        console.log("Update Totals...");

        this.displayWeight.innerText = CustomBuilder.formatWeight(this.weight);
        this.displayCost.innerText = Shopify.formatMoney(this.cost, "");
    }

    // Formats Weight
    static formatWeight(weight) {
        let weightLbs = (parseFloat(weight) / 453.59237).toFixed(2).replace('.00', '');
        let weightKgs = (parseFloat(weight) / 1000).toFixed(2).replace('.00', '');

        return `${weightLbs} Lb (${weightKgs} Kg)`;
    }

    // Updates builder bikologi image
    updateBuilderCanvas() {
        console.log('----------------------------------------------------------');
        console.log("updateBuilderCanvas...");
        // Placeholder for logic to update the builder canvas image
    }

    // Restarts builder to first step
    restartBuilder() {
        console.log('----------------------------------------------------------');
        console.log("Restarting builder...");

        // Reset all builder variables to default
        this.currentStep = 0;
        this.currentStepJson = {};
        this.selections = [];
        this.productsToAdd = [];
        this.discounts = {};
        this.cost = 0;
        this.weight = 0;
        this.stepVariantDependency = [];
        this.currentStepTitle = '';
        this.productDetailsContent.innerHTML = '';
        this.clearCanvas();
        this.cleanUpVariantListeners();
        this.updateTotals();

        // Build first step
        this.buildStep(0);
    }

    // Displays build summary quick-drawer
    buildSummary() {
        console.log('----------------------------------------------------------');
        console.log("Showing build summary...");

        // Pick out only the real selections
        const items = this.selections.filter(i => i);

        // Render a table
        const html = `
          <table class="builder-summary specs-table">
            <thead>
              <tr>
                <th>Step</th>
                <th>Selection</th>
                <th>Price / Weight</th>
              </tr>
            </thead>
            <tbody>
              ${items.map(item => `
                <tr>
                  <td>${item.short_title}</td>
                  <td>${item.selection}</td>
                  <td>${item.price != null ? Shopify.formatMoney(item.price, "") : '—'}<br>
                      ${item.weight != null ? CustomBuilder.formatWeight(item.weight) : '—'}</td>
                </tr>
              `).join('')}
              <tr>
                  <td colspan="2">Total</td>
                  <td>${Shopify.formatMoney(this.cost)}<br>
                      ${CustomBuilder.formatWeight(this.weight)}</td>
            </tbody>
          </table>
        `;

        // Inject table into drawer
        this.summaryDrawer.querySelector('#build-summary-container').innerHTML = html;

        // Set source of summary builder image
        const builderImageSrc = document.getElementById('builder-image').getAttribute('data-src');
        this.summaryDrawer.querySelector('#build-summary-image').src = builderImageSrc;

        // Add event listener for print button
        const printButton = this.summaryDrawer.querySelector('#build-summary-print-button');
        if (printButton) {
            printButton.addEventListener('click', () => {
                window.print();
            });
        }

        // Open drawer
        this.summaryDrawer.open = true;
    }

    // Toggles visiblity af details panels and enables or disables control buttons
    toggleControlsAndDetails(stepIndex) {
        let currentStepJsonTitle = this.currentStepTitle;
        if (stepIndex === 0) {
            // First step

            // Check if first step content is hidden and if so show it
            if (this.firstStepContent.classList.contains('hidden')) {
                this.firstStepContent.classList.toggle('hidden');
            }

            // Check if product details content is hidden and if not hide it
            if (!this.productDetailsContent.classList.contains('hidden')) {
                this.productDetailsContent.classList.toggle('hidden');
            }

            // Check if last details content is hidden and if not hide it
            if (!this.finalStepContent.classList.contains('hidden')) {
                this.finalStepContent.classList.toggle('hidden');
            }

            // Check if completed content is hidden and if not hide it
            if (!this.completeStepContent.classList.contains('hidden')) {
                this.completeStepContent.classList.toggle('hidden');
            }

            // Check if product selection container is hidden and if not hide it
            if (this.selectionContainer.classList.contains('hidden')) {
                this.selectionContainer.classList.toggle('hidden');
            }

            // Disable all controls
            document.querySelectorAll("#builder-controls button").forEach((button) => {
                button.disabled = true;
            });
        } else if (currentStepJsonTitle === 'Final Details') {
            // Last step

            // Check if last step content is hidden and if so show it
            if (this.finalStepContent.classList.contains('hidden')) {
                this.finalStepContent.classList.toggle('hidden');
            }

            // Check if product details content is hidden and if not hide it
            if (!this.productDetailsContent.classList.contains('hidden')) {
                this.productDetailsContent.classList.toggle('hidden');
            }

            // Check if product selection container is hidden and if not hide it
            if (!this.selectionContainer.classList.contains('hidden')) {
                this.selectionContainer.classList.toggle('hidden');
            }

            if (this.builderType === 'wheel') {
                // Enable complete build button if wheel builder
                this.completeButton.disabled = false;
            }

        } else {
            // Any step other than first or last

            // Enable all controls
            document.querySelectorAll("#builder-controls button").forEach((button) => {
                // if (button.id === 'skip-button' && this.currentStepRequired) {
                if (button.id === 'skip-button') {
                    button.disabled = true;
                } else {
                    button.disabled = false;
                }
            });

            // Check if first step content is hidden and if not hide it
            if (!this.firstStepContent.classList.contains('hidden')) {
                this.firstStepContent.classList.toggle('hidden');
            }
            // Check if final step content is hidden and if not hide it
            if (!this.finalStepContent.classList.contains('hidden')) {
                this.finalStepContent.classList.toggle('hidden');
            }

            // Check if product details content is hidden and if so show it
            if (this.productDetailsContent.classList.contains('hidden')) {
                this.productDetailsContent.classList.toggle('hidden');
            }

            // Check if product selection container is hidden and if not hide it
            if (this.selectionContainer.classList.contains('hidden')) {
                this.selectionContainer.classList.toggle('hidden');
            }
        }
    }

    // Displays completed build confirmation panel
    loadConfirmationPanel() {

        // Check if last details content is hidden and if not hide it
        if (!this.finalStepContent.classList.contains('hidden')) {
            this.finalStepContent.classList.toggle('hidden');
        }

        // Display confirmation 
        if (this.completeStepContent.classList.contains('hidden')) {
            this.completeStepContent.classList.toggle('hidden');
        }

        document.getElementById('previous-button').disabled = true;
    }

    static sanitizeInput(input) {
        return input
            .replace(/[^a-zA-Z0-9\s\-\.\"\'\/cmCMinIN]/g, '')
            .trim()
            .substring(0, 100);
    }

    static sanitizeTextAreaInput(input) {
        return input
            .replace(/[^a-zA-Z0-9\s\-\.\"\'\/,?!()@:#$%&*]/g, '')
            .trim()
            .substring(0, 1000);
    }

    static isValidNumeric(value) {
        return value && !isNaN(value) && parseFloat(value) > 0;
    }

    toggleValidationMessage(inputEl, isValid, message) {
        const errorEl = inputEl.nextElementSibling;
        if (!errorEl || !errorEl.classList.contains('validation-error')) return;

        const dirty = inputEl.dataset.dirty === 'true';

        // If not dirty, don't show anything yet
        if (!dirty) {
            errorEl.style.display = 'none';
            inputEl.classList.remove('input-error');
            return;
        }

        // If dirty and invalid (or blank), show error
        if (!isValid || !inputEl.value.trim()) {
            errorEl.textContent = message;
            errorEl.style.display = 'block';
            inputEl.classList.add('input-error');
        } else {
            errorEl.style.display = 'none';
            inputEl.classList.remove('input-error');
        }
    }


    // Function to validate the inputs
    validateInputs() {
        const weightRaw = this.weightInput.value;
        const heightRaw = this.heightInput.value;
        const saddleRaw = this.saddleInput.value;

        const weight = CustomBuilder.sanitizeInput(weightRaw);
        const height = CustomBuilder.sanitizeInput(heightRaw);
        const saddle = CustomBuilder.sanitizeInput(saddleRaw);

        // Set sanitized values back in case the user pasted something weird
        this.weightInput.value = weight;
        this.heightInput.value = height;
        this.saddleInput.value = saddle;

        const weightValid = CustomBuilder.isValidNumeric(weight);
        const heightValid = CustomBuilder.isValidNumeric(height);
        const saddleValid = CustomBuilder.isValidNumeric(saddle);

        this.toggleValidationMessage(this.weightInput, weightValid, this.weightErrorMessage);
        this.toggleValidationMessage(this.heightInput, heightValid, this.heightErrorMessage);
        this.toggleValidationMessage(this.saddleInput, saddleValid, this.saddleErrorMessage);

        this.completeButton.disabled = !(weightValid && heightValid && saddleValid);
    }

    // Handle variant changes
    handleVariantChange(event) {
        console.log("Variant Changed!!!", event);
        //const builderProductDetails = document.getElementById('builder-product-details');
        const productForm = document.querySelector('.builder-product-form');

        if (productForm) {
            const bikologiData = productForm?.dataset?.bikologi;
            if (bikologiData) {
                try {
                    const productData = JSON.parse(bikologiData);
                    if (productData.id && productData.metafields?.bikologi) {
                        this.activeBindings[productData.id] = productData.metafields.bikologi;
                    }
                    // Wait for DOM to reload before calling handleProductBuild
                    setTimeout(() => {
                        this.handleProductBuild(productData);
                    }, 0);
                } catch (err) {
                    console.error('Error parsing bikologi data:', err);
                }
            } else {
                console.warn('No bikologi data found on productForm!');
            }
        } else {
            console.warn('No productForm found inside builderProductDetails!');
        }
    }

    // Remove event listeners and disconnect mutation observers
    cleanUpVariantListeners() {
        console.log('Cleaning up event listeners and mutation observers');

        if (this.variantFilterObserver) {
            this.variantFilterObserver.disconnect();
            this.variantFilterObserver = null;
        }
    }

    // Returns promise after filtering out variant swatches based on builder variant dependencies
    filterVariants(selectFirstVariant = false) {
        console.log('Filtering Variants...');

        // Hide product details on load to prevent flickering from variant filtering
        const sectionBuilderDetails = document.getElementById('shopify-section-builder-product-details');
        if (sectionBuilderDetails && selectFirstVariant) {
            sectionBuilderDetails.classList.add('hidden');
        }

        return new Promise((resolve) => {

            const productForm = document.querySelector('.builder-product-form');
            if (!productForm) {
                console.warn('filterVariants(): Product form not found!');
                return;
            }

            // Grab all optionSelectors and filter out any options that don't contain at at least
            // one variant dependency as we don't want to filter these
            let filteredSwatches = [];
            const optionSelectors = productForm.querySelectorAll('.product-form__option-selector');
            optionSelectors.forEach(optionSelector => {

                const swatches = optionSelector.querySelectorAll('.block-swatch');

                // Determine if this option group should be filtered:
                const groupNeedsFilter = Array.from(swatches).some(swatch => {
                    const label = swatch.querySelector('label');
                    if (!label) return false;
                    const labelHandle = CustomBuilder.handleize(label.innerText || label.textContent);
                    // The group is filtered only if at least one swatch's label matches one of the dependencies.
                    return this.stepVariantDependency.includes(labelHandle);
                });

                // If the group needs filtering, process its swatches and add them to filteredSwatches array
                if (groupNeedsFilter) {
                    swatches.forEach(swatch => {
                        const label = swatch.querySelector('label');
                        if (label) {
                            const labelHandle = CustomBuilder.handleize(label.innerText || label.textContent);
                            if (!this.stepVariantDependency.includes(labelHandle)) {
                                swatch.classList.add("hidden");
                            } else {
                                filteredSwatches.push(swatch);
                            }
                        }
                    });
                }
            });

            // Select the first filtered variant - required when initially loading product details
            if (selectFirstVariant && filteredSwatches.length > 0) {
                const firstSwatchInput = filteredSwatches[0].querySelector('input[type="radio"]');
                if (firstSwatchInput) {
                    // Check the radio input and dispatch a change event so listeners are triggered
                    firstSwatchInput.checked = true;
                    firstSwatchInput.dispatchEvent(new Event('change', { bubbles: true }));
                }
            } else if (sectionBuilderDetails) {
                // Unhide product details when done filtering
                sectionBuilderDetails.classList.remove('hidden');
            }

            // Resolve the Promise when filtering is complete.
            resolve();
        });
    }

    setupVariantFilterObserver() {
        const productForm = document.querySelector('.builder-product-form');
        if (!productForm) return;

        // Create a MutationObserver that calls variant filter
        const observer = new MutationObserver((mutationsList) => {
            console.log('Mutation Observer calling filter variants');
            this.filterVariants();
        });

        // Configuration: observe added nodes and changes in all descendant elements
        const config = { childList: true, subtree: true };

        // Start observing the container
        observer.observe(productForm, config);

        // Store reference to the observer so we can disconnect it later
        this.variantFilterObserver = observer;
    }

    // BIKOLOGI REFACTOR

    // Handle variant selection and build canvas
    handleProductBuild(productData) {
        console.log('[BIKOLOGI] handleProductBuild() triggered');
        console.log('[BIKOLOGI] productData:', productData);

        // 1. Bind color (same logic from bindColor)
        this.bindColor(productData);

        // 2. Add productData to activeBuild
        // this.activeBuild[productData.handle || productData.id] = productData;

        // 3. Build the bike
        this.buildBike();
    }

    // Bind builder selection to 
    bindColor(productData) {
        console.log('[BIKOLOGI] bindColor');

        // Get JSON variant data from product form
        const variantScript = document.querySelector("script[data-variant]");

        if (!variantScript) {
            console.warn('BIKOLOGI: No variant data found in form.');
            return;
        }

        console.log('[BIKOLOGI] Variant data found!');

        const variant = JSON.parse(variantScript.textContent);
        const variantId = variant.id;
        const variantTitle = variant.builder_data.variant_title_handle;
        const handle = this.currentStepJson.handle;

        if (!handle) {
            console.warn('[BIKOLOGI] currentStepJson.handle is missing.');
            return;
        }

        if (!this.activeBuild[handle]) {
            this.activeBuild[handle] = {};
        }

        this.activeBuild[handle]['id'] = productData.id;
        this.activeBuild[handle]['variant'] = variantId;
        this.activeBuild[handle]['color'] = variantTitle.toLowerCase().replace('-', ' ');

        console.log('[BIKOLOGI] Updated activeBuild:', this.activeBuild);
    }

    // Find component in activeBuild object
    findComponent(component) {
        // console.log('FIND COMPONENT: ', component);
        // console.log('Active Build Component: ', this.activeBuild[component]);
        return this.activeBuild[component];
    }

    // Removes current item from Active Build
    removeCurrentItemFromCanvas() {
        // Remove current item from active build
        console.log('[BIKOLOGI] Removing item from Active Build object:', this.activeBuild[this.currentStepJson.handle]);
        if (this.activeBuild[this.currentStepJson.handle]) {
            // console.log(`BIKOLOGI - Removing ${this.currentStepJson.handle} from active build`);
            delete this.activeBuild[this.currentStepJson.handle];
            this.buildBike();
        }
    }

    // Clears canvas
    clearCanvas() {
        // console.log('[BIKOLOGI] Entering clearCanvas()');

        // Check for errors first
        if (!this.canvas) {
            console.error('Canvas not found.');
            return;
        }

        const ctx = this.canvas.getContext('2d');

        if (!ctx) {
            console.error('Context not found.');
            return;
        }

        // Reset global variables and clear canvas
        this.activeBuild = {};
        this.existingBuild = {};
        this.imgStore = {};
        ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }

    // Obtain the mouse position on the canvas
    getMousePos(canvas, evt) {
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        return [
            (evt.clientX - rect.left) * scaleX,
            (evt.clientY - rect.top) * scaleY
        ];
    }

    // Reset spoke pattern
    resetSpokePattern() {
        for (let i = 1; i <= 32; i++) {
            delete this.spokePattern['rearCustomSpokes' + i];
            delete this.spokePattern['frontCustomSpokes' + i];
        }

        // Trigger bike rebuild (we’ll refactor this.buildBike() next)
        this.buildBike();
    }

    // Not sure what this does
    inPolygon(point, vs) {
        // ray-casting algorithm based on
        // http://www.ecse.rpi.edu/Homepages/wrf/Research/Short_Notes/pnpoly.html

        let x = point[0], y = point[1];
        let inside = false;

        for (let i = 0, j = vs.length - 1; i < vs.length; j = i++) {
            let xi = vs[i][0], yi = vs[i][1];
            let xj = vs[j][0], yj = vs[j][1];

            let intersect = ((yi > y) != (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
            if (intersect) inside = !inside;
        }

        return inside;
    };

    // Throttle for some reason, not sure what this does
    static throttle(func, interval) {
        let lastCall = 0;

        return function (...args) {
            const now = Date.now();

            if (lastCall + interval < now) {
                lastCall = now;
                return func.apply(this, args);
            }
        };
    }

    // Get component binding
    getBinding(component) {
        if (
            component &&
            this.activeBindings[component.id] &&
            this.activeBindings[component.id].binding
        ) {
            const bindingData = JSON.parse(this.activeBindings[component.id].binding);

            if (component.variant) {
                return bindingData[component.variant];
            } else {
                const firstKey = Object.keys(bindingData)[0];
                return bindingData[firstKey];
            }
        }

        console.warn('No binding found for component:', component);
        return '';
    }

    // Sets up functions to draw bike
    buildBike() {
        console.log('----------------------------------------------------------');
        console.log('[BIKOLOGI] buildBike() START');

        try {
            // Get or create canvas
            this.getOrCreateCanvas();

            // Build the params object here (coming next!)
            const imageUrlParams = this.buildImageUrlParams();

            // Call renderImage() with params
            // console.log('[BIKOLOGI] Params for renderImage:', imageUrlParams);
            this.renderImage(imageUrlParams);

            // Update the canvas data-src
            this.updateCanvasDataSrc(imageUrlParams);

            // Save snapshot of active build (deep clone)
            this.existingBuild = JSON.parse(JSON.stringify(this.activeBuild));

        } catch (err) {
            console.error('Error in buildBike():', err);
        }
    }

    // Sets everything up to render canvas image
    async renderImage(query) {
        console.log('----------------------------------------------------------');
        console.log('[BIKOLOGI] Running renderImage() with query:', query);

        const promises = [];

        // Normalize color values and find any missing bikes/components
        for (const key in query) {
            if (key.includes('Color') && query[key] !== undefined) {
                query[key] = query[key].toLowerCase();
            }

            if (key.includes('ID')) {
                const id = query[key];

                // Check if we already have this bike/component loaded
                const bikeExists = !!this.bikes[id];
                const componentExists = !!this.components[id];

                if (!bikeExists && !componentExists) {
                    promises.push(this.getItem(key, id));
                }
            }
        }

        try {
            // Wait for all missing items to be fetched
            const data = await Promise.all(promises);

            // Add fetched bikes/components to memory
            data.forEach(item => {
                if (item && item._id) {
                    if (item.canvasMap) {
                        this.bikes[item._id] = item;
                    } else {
                        this.components[item._id] = item;
                    }
                }
            });

            // Parse bike or wheel and render
            const buildProps = this.builderType === 'bike'
                ? this.parseBike(query)
                : this.parseWheel(query);

            this.renderImages(buildProps, query);
        } catch (error) {
            console.error('renderImage() - Error fetching items:', error);
        }
    }

    // Renders images on canvas
    async renderImages(bike, query) {
        console.log('[BIKOLOGI] Entering renderImages()', { bike, query });
        const bikeBuilder = this.builderType === 'bike';

        // Check for errors first
        if (!this.canvas) {
            console.error('Canvas not found.');
            return;
        }

        const ctx = this.canvas.getContext('2d');

        if (!ctx) {
            console.error('Context not found.');
            return;
        }

        let bikeMap;

        if (bikeBuilder) {
            if (!bike.reference?.bike?.canvasMap) {
                console.error('[BIKOLOGI] No canvasMap found for this bike:', bike);
                return;
            }

            bikeMap = structuredClone(bike.reference.bike.canvasMap);
        } else {
            if (this.selections[1].value === 'wheelset') {
                console.log('USE WHEELSET MAP:', this.selections[1].value);
                bikeMap = {
                    "wheels": [
                        {
                            "left": 84,
                            "top": "150",
                            "width": "650"
                        },
                        {
                            "left": 859,
                            "top": "150",
                            "width": "650"
                        }
                    ],
                    "rear tire": {
                        "left": "30",
                        "top": "96",
                        "width": "760"
                    },
                    "front tire": {
                        "left": "805",
                        "top": 96,
                        "width": 760
                    },
                    "rear rotor": {
                        "left": 309,
                        "top": 375,
                        "width": "200"
                    },
                    "front rotor": {
                        "left": "1080",
                        "top": 374,
                        "width": "200"
                    },
                    "cassette": {
                        "left": 310,
                        "top": 376,
                        "width": "200"
                    },
                    "bigCassette": {
                        "left": 299,
                        "top": 366,
                        "width": "220"
                    }
                }
                //   $('#builder-image').addClass('wheelset');

            } else {
                console.log('USE SINGLE WHEEL MAP');
                bikeMap = {
                    "wheels": [
                        {
                            "left": "475",
                            "top": "150",
                            "width": "650"
                        },
                        {
                            "left": "475",
                            "top": "150",
                            "width": "650"
                        }
                    ],
                    "rear tire": {
                        "left": "420",
                        "top": 97,
                        "width": "760"
                    },
                    "front tire": {
                        "left": 420,
                        "top": 97,
                        "width": 760
                    },
                    "rear rotor": {
                        "left": "700",
                        "top": 375,
                        "width": "200"
                    },
                    "front rotor": {
                        "left": "700",
                        "top": 374,
                        "width": "200"
                    },
                    "cassette": {
                        "left": 700,
                        "top": 376,
                        "width": "200"
                    },
                    "bigCassette": {
                        "left": "690",
                        "top": 366,
                        "width": "220"
                    }
                }
                // $('#builder-image').addClass('single');
            }
        }

        // Optional preloader show
        // const preloader = document.getElementById('builder-preloader');
        // preloader.style.display = 'block';

        try {
            this.imgStore = await this.preloadCanvasImages(bike);
            // await this.preloadCanvasImages(bike);
            this.drawCanvas(ctx, bike, bikeMap, query);
        } catch (error) {
            console.error('Error preloading images', error);
        }

        // Optional preloader hide
        // preloader.style.display = 'none';
    }

    // Loads Bikologi images into memory
    async preloadCanvasImages(srcs) {
        console.log('----------------------------------------------------------');
        console.log('[BIKOLOGI] Running preloadCanvasImages() with sources:', srcs);

        const promises = [];
        const imgStore = {};  // You may want to make this a class property if reused

        // Helper to load a single image and store it
        const loadImage = (url) => {
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.onload = () => {
                    imgStore[url] = img;
                    resolve(img);
                };
                img.onerror = () => {
                    console.error(`Failed to load image: ${url}`);
                    reject(new Error(`Failed to load image: ${url}`));
                };
                img.src = url;  // Bind onload before setting src
            });
        };

        for (const key in srcs) {
            if (key !== 'reference') {
                const url = srcs[key];
                if (url) {
                    promises.push(loadImage(url));
                } else {
                    console.warn(`No URL found for key: ${key}`);
                }
            }
        }

        try {
            await Promise.all(promises);
            console.log('[BIKOLOGI] ✅ All images preloaded successfully', imgStore);
            return imgStore;  // return imgStore if you want to use it later
        } catch (error) {
            console.error('[BIKOLOGI] ❌ Error preloading images:', error);
            throw error; // Bubble up the error to the caller
        }
    }

    // Lays down local pngs to be positioned and displayed
    paintCanvas(image, ctx, imgMap) {
        let props = imgMap,
            data;

        if (image === undefined) {
            // fail
        } else {
            ctx.save();

            if (props.angle) {
                ctx.rotate(parseFloat(props.angle));
            }

            if (props.under) {
                ctx.globalCompositeOperation = 'destination-over';
            }

            if (props.flip) {
                ctx.scale(-1, 1);
            }

            if (props.height) {
                ctx.drawImage(this.imgStore[image], parseInt(props.left), parseInt(props.top), parseInt(props.height) * parseInt(this.imgStore[image].width) / parseInt(this.imgStore[image].height), parseInt(props.height));
            } else { //default horizontal based ratio
                ctx.drawImage(this.imgStore[image], parseInt(props.left), parseInt(props.top), parseInt(props.width), parseInt(props.width) * parseInt(this.imgStore[image].height) / parseInt(this.imgStore[image].width));
            }

            ctx.restore();
        }
    }

    // Draws to canvas
    drawCanvas(ctx, bike, bikeMap, query) {
        console.log('[BIKOLOGI] Entering drawCanvas()', { bike, query });

        // Clear the canvas first
        ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

        // Helpers to make the code cleaner
        const paint = (image, map) => {
            if (!image || !map) {
                console.log('[BIKOLOGI] No Image or No Map!!!!');
                return;
            }
            // console.log('[BIKOLOGI] - Paint image and map', { image, map });
            this.paintCanvas(image, ctx, map);
        };

        const paintCassette = () => {
            if (!query.cassetteID) return;
            if (bike.reference.cassette?.speeds?.includes('1 x 12')) {
                bikeMap.bigCassette.under = true;
                paint(bike.cassette, bikeMap.bigCassette);
            } else {
                bikeMap.cassette.under = true;
                paint(bike.cassette, bikeMap.cassette);
            }
        };

        const paintSpider = () => {
            if (query.spiderID) paint(bike.spider, bikeMap.spider);
        };

        const paintCrank = () => {
            if (!query.cranksetID) return;
            if (bike.crankset) paint(bike.crankset, bikeMap.crankset);
            if (bike.crankTop) paint(bike.crankTop, bikeMap.crankset);
        };

        const paintChainRing = () => {
            if (query.cranksetID && bike.crankBottom) paint(bike.crankBottom, bikeMap.crankset);
            if (query.chainRingID) paint(bike.chainRing, bikeMap['chain ring']);
        };

        const paintDerailleur = () => {
            if (query.rearDerailleurID) paint(bike.rearDerailleur, bikeMap['rear derailleur']);
            if (query.chainID) paint(bike.chainDerailleur, bikeMap['rear derailleur']);
        };

        const paintChainGuide = (position) => {
            if (!query.chainGuideID) return;
            if (position === 'top') paint(bike.chainGuideTop, bikeMap['chain guide']);
            if (position === 'bottom') paint(bike.chainGuideBottom, bikeMap['chain guide']);
        };

        const paintChainBottom = (hasGuide) => {
            if (hasGuide && bike.reference.chainGuide?.bottomRoller) {
                paint(bike.chainBottomRoller, bikeMap.chain.roller);
            } else {
                paint(bike.chainBottom, bikeMap.chain.bottom);
            }
        };

        const paintChainOnRing = (oval, hasGuide) => {
            if (!oval) return;

            if (oval.isOval) {
                if (hasGuide && bike.reference.chainGuide?.bottomRoller) {
                    paint(bike.chainRingOvalRoller, bikeMap.chain.ring);
                } else {
                    paint(bike.chainRingOval, bikeMap.chain.ring);
                }
            } else {
                if (hasGuide && bike.reference.chainGuide?.bottomRoller) {
                    paint(bike.chainRingRoller, bikeMap.chain.ring);
                } else {
                    paint(bike.chainOnRing, bikeMap.chain.ring);
                }
            }

            if (bike.topRoller) {
                paint(bike.topRoller, bikeMap.chain.topRoller);
            }
        };

        const bikeBuilder = this.builderType === 'bike';

        if (bikeBuilder) {

            // === FRAME ===
            if (bikeMap.color.angle) {
                bikeMap.color.angle = parseFloat(bikeMap.color.angle);
            }
            paint(bike.frame, bikeMap.color);

            // === FORK ===
            if (query.forkID) {
                if (bike.forkCrown) {
                    bikeMap.steerer.under = true;
                    bikeMap.forkCrown.under = true;
                    paint(bike.forkCrown, bikeMap.forkCrown);
                    paint(bike.forkSteerer, bikeMap.steerer);
                } else if (bike.fork) {
                    bikeMap.steerer.under = true;
                    paint(bike.forkSteerer, bikeMap.steerer);
                }
            }

            // === HEADSET ===
            if (query.headsetID && !bike.reference.bike.hsInclude && bike.reference.bike.manufacturer !== 'Evil') {
                paint(bike.headsetUpper, bikeMap.headset[1]);
                paint(bike.headsetLower, bikeMap.headset[0]);
            }

            // === SHOCK ===
            if (query.shockID) {
                bikeMap.shock.under = true;
                paint(bike.shock, bikeMap.shock);
            }

            // === BOTTOM BRACKET ===
            if (query.bottomBracketID) {
                paint(bike.bottomBracket, bikeMap.bb);
            }

            // === GEARBOX CHAIN ===
            if (bike.reference.bike.bottomBracket === 'gearbox' && query.chainID) {
                const chainColor = query.chainColor || bike.reference.chain?.images.builder[0].color;
                paint(bike[`chain-${chainColor}`], bikeMap.color);
                paint(bike.frameStays, bikeMap.color);
            } else {
                const chainGuidePresent = !!query.chainGuideID;

                if (query.rearDerailleurID && (query.chainRingID || bike.crankTop) && query.cassetteID) {
                    if (query.chainID) {
                        paintCassette();
                        paintChainGuide('bottom');
                        paintChainRing();
                        paint(bike.chainTop, bikeMap.chain.top);
                        paintChainOnRing(query.chainRingID ? bike.reference.chainRing : bike.reference.crankset, chainGuidePresent);
                        paintSpider();
                        paintCrank();
                        bikeMap.color.under = false;
                        paint(bike.frameStays, bikeMap.color);
                        paintDerailleur();
                        paintChainBottom(chainGuidePresent);
                        paintChainGuide('top');
                    } else {
                        paintCassette();
                        paintDerailleur();
                        paintChainGuide('bottom');
                        paintChainRing();
                        paintSpider();
                        paintCrank();
                        paintChainGuide('top');
                    }
                } else {
                    paintCassette();
                    paintDerailleur();
                    paintChainGuide('bottom');
                    paintChainRing();
                    paintSpider();
                    paintCrank();
                    paintChainGuide('top');
                }
            }

            // === WHEELS ===
            if (query.wheelsID) {
                if (bike.reference.wheels?.wheelSize === '27.5+') {
                    bikeMap.plusWheels = [
                        structuredClone(bikeMap.wheels[0]),
                        structuredClone(bikeMap.wheels[1]),
                    ];
                    bikeMap.plusWheels.forEach((wheel, i) => {
                        wheel.width = parseInt(wheel.width) - 10;
                        wheel.left = parseInt(wheel.left) + 5;
                        wheel.top = parseInt(wheel.top) + 5;
                        wheel.under = true;
                        paint(bike.wheels, wheel);
                    });
                } else {
                    bikeMap.wheels.forEach(wheel => {
                        wheel.under = true;
                        paint(bike.wheels, wheel);
                    });
                }
            }
        }

        // === CUSTOM WHEELS ===
        console.log('DRAW CANVAS bike.referenc', bike.reference);
        const fOffset = parseInt(bike.reference.frontRim?.nippleOffset || 0);
        const rOffset = parseInt(bike.reference.rearRim?.nippleOffset || 0);

        console.log('DRAW CANVAS FRONT AND REAR OFFSET:', { fOffset, rOffset });

        bikeMap.customWheels = [
            structuredClone(bikeMap.wheels[0]),
            structuredClone(bikeMap.wheels[1]),
        ];

        // console.log('[BIKOLOGI] drawCanvas -- bikeMap.customWheels:', bikeMap.customWheels);

        bikeMap.customWheels[0].under = true; // Rear
        bikeMap.customWheels[1].under = true; // Front

        // Check for Enve or Industry Nine rims and make adjustments
        const frontRim = bike.reference.frontRim;
        if (frontRim && (frontRim.manufacturer === 'Enve' || frontRim.manufacturer === 'Industry Nine')) {
            // Apply size/position adjustments to both front and rear wheels
            [bikeMap.customWheels[0], bikeMap.customWheels[1]].forEach(wheel => {
                wheel.width = parseInt(wheel.width) + 8;
                wheel.left = parseInt(wheel.left) - 4;
                wheel.top = parseInt(wheel.top) - 4;
            });
        }

        // --- FRONT WHEEL COMPONENTS ---

        // Front Rim
        if (query.frontRimID) {
            paint(bike.frontRim, bikeMap.customWheels[1]);
        }

        // Front Spokes
        const isFrontI9 = bike.reference.frontSpoke?.manufacturer === 'Industry Nine';
        if (query.frontSpokeID) {
            if (!isFrontI9) {
                bikeMap.customWheels[1].width = parseInt(bikeMap.customWheels[1].width) - (fOffset * 2);
                bikeMap.customWheels[1].left = parseInt(bikeMap.customWheels[1].left) + fOffset;
                bikeMap.customWheels[1].top = parseInt(bikeMap.customWheels[1].top) + fOffset;
            }
            paint(bike.frontSpoke, bikeMap.customWheels[1]);
        }

        // Front Nipples
        if (query.frontNippleID) {
            if (!['Enve', 'Industry Nine'].includes(frontRim?.manufacturer)) {
                paint(bike.frontNipple, bikeMap.customWheels[1]);
            }
        }

        // Front Hub
        if (query.frontHubID) {
            paint(bike.frontHub, bikeMap.customWheels[1]);
        }

        // Front Valve Stem
        if (query.frontValveStemID) {
            bikeMap.valves = [
                structuredClone(bikeMap.wheels[0]),
                structuredClone(bikeMap.wheels[1])
            ];

            bikeMap.valves[1].under = true;
            bikeMap.valves[1].top -= isFrontI9 ? 8 : 12;

            paint(bike.frontValveStem, bikeMap.valves[1]);
        }

        // --- REAR WHEEL COMPONENTS ---

        // Rear Rim
        if (query.rearRimID) {
            paint(bike.rearRim, bikeMap.customWheels[0]);
        }

        // Rear Spokes
        const isRearI9 = bike.reference.rearSpoke?.manufacturer === 'Industry Nine';
        if (query.rearSpokeID) {
            if (!isRearI9) {
                bikeMap.customWheels[0].width = parseInt(bikeMap.customWheels[0].width) - (rOffset * 2);
                bikeMap.customWheels[0].left = parseInt(bikeMap.customWheels[0].left) + rOffset;
                bikeMap.customWheels[0].top = parseInt(bikeMap.customWheels[0].top) + rOffset;
            }
            paint(bike.rearSpoke, bikeMap.customWheels[0]);
        }

        // Rear Nipples
        const rearRim = bike.reference.rearRim;
        if (query.rearNippleID) {
            if (!['Enve', 'Industry Nine'].includes(rearRim?.manufacturer)) {
                paint(bike.rearNipple, bikeMap.customWheels[0]);
            }
        }

        // Rear Hub
        if (query.rearHubID) {
            paint(bike.rearHub, bikeMap.customWheels[0]);
        }

        // Rear Valve Stem
        if (query.rearValveStemID) {

            bikeMap.valves = [
                structuredClone(bikeMap.wheels[0]),
                structuredClone(bikeMap.wheels[1])
            ];

            bikeMap.valves[0].under = true;
            bikeMap.valves[0].top -= isFrontI9 ? 8 : 12;

            paint(bike.frontValveStem, bikeMap.valves[0]);
        }

        // === TIRES ===
        if (query.frontTireID) {
            bikeMap['front tire'].under = true;
            paint(bike.frontTire, bikeMap['front tire']);
        }

        if (query.rearTireID) {
            bikeMap['rear tire'].under = true;
            paint(bike.rearTire, bikeMap['rear tire']);
        }

        if (query.saddleID) {
            bikeMap['saddle'].under = true;
            paint(bike.saddle, bikeMap['saddle']);
        }

        if (query.seatPostID) {
            bikeMap['seatpost'].under = true;
            paint(bike.seatPost, bikeMap['seatpost']);
        }

        if (query.pedalID) {
            paint(bike.pedal, bikeMap['pedals']);
        }

        if (query.forkID && bike.forkLowers) {
            bikeMap['forkLowers'].under = true;
            paint(bike.forkBrakeMount, bikeMap['forkLowers']);
            bikeMap['forkLowers'].under = false;
            paint(bike.forkLowers, bikeMap['forkLowers']);
        } else if (query.forkID && bike.fork) {
            bikeMap['fork'].under = true;
            paint(bike.forkBrakeMount, bikeMap['fork']);
            bikeMap['fork'].under = false;
            paint(bike.fork, bikeMap['fork']);
        }

        if (query.rotorID) {
            bikeMap['rear rotor'].under = true;
            bikeMap['front rotor'].under = true;
            paint(bike.rotor, bikeMap['front rotor']);
            paint(bike.rotor, bikeMap['rear rotor']);
        }

        if (query.frontBrakeID) {
            bikeMap['caliper front'].under = true;
            paint(bike.frontBrake, bikeMap['caliper front']);
        }

        if (query.frontRotorID) {
            bikeMap['front rotor'].under = true;
            paint(bike.frontRotor, bikeMap['front rotor']);
        }

        if (query.rearBrakeID) {
            bikeMap['caliper rear'].under = true;
            paint(bike.rearBrake, bikeMap['caliper rear']);
        }

        if (query.rearRotorID) {
            bikeMap['rear rotor'].under = true;
            paint(bike.rearRotor, bikeMap['rear rotor']);
        }

        if (bikeBuilder) {
            bikeMap['color'].under = true;
            paint(bike.frameBrakeMount, bikeMap['color']);

            // === FINAL COMPONENTS ===
            if (query.stemID) {
                if (query.forkID && !bike.fork) {
                    paint(bike.stem, bikeMap.stem);
                    paint(bike.frameDetail, bikeMap.cockpit);
                    bikeMap.forkLowers.under = false;
                    paint(bike.forkLowers, bikeMap.forkLowers);
                } else if (query.forkID && bike.fork) {
                    paint(bike.stem, bikeMap.stem);
                    bikeMap.cockpit.under = true;
                    paint(bike.dhFrameDetail, bikeMap.cockpit);
                    paint(bike.fork, bikeMap.fork);
                } else {
                    paint(bike.stem, bikeMap.stem);
                    paint(bike.frameDetail, bikeMap.cockpit);
                }
            }
        }

        if (query.background === 'fanatik') {
            paint(bike.background, {
                left: 0,
                top: 0,
                width: 1600,
                height: 950,
                under: true
            });
        }

        console.log('[BIKOLOGI] drawCanvas() complete');
    }

    // Gets item data from Bikologi
    async getItem(key, id) {
        if (!id) return {};

        const itemType = key === 'bikeID' ? 'bike' : 'component';

        try {
            const response = await fetch(`https://bikologi-api.herokuapp.com/${itemType}/${id}`, {
                method: 'GET',
                headers: {
                    'Authorization': '37702ba794eee08e64deb3d2dcff4ea9'
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch ${itemType} with ID ${id}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error(`getItem() - Error fetching ${itemType} ${id}:`, error);
            return {};
        }
    }

    // Gets or creates canvas
    getOrCreateCanvas() {
        // Get or create canvas
        this.canvas = document.getElementById('builder-image');

        if (!this.canvas) {
            this.canvas = document.createElement('canvas');
            this.canvas.id = 'builder-image';
            this.canvas.width = 1600;
            this.canvas.height = 950;

            document.querySelector('#builder-logo').appendChild(this.canvas);
        }
    }

    // Updates canvas data source
    updateCanvasDataSrc(imageUrlParams) {
        // Update canvas data-src (wheel-builder or trail-bike)

        // Check if canvas is defined
        if (!this.canvas) {
            console.warn('Canvas not initialized');
            return;
        }

        if (this.builderType === 'wheel') {
            let bikologiImageUrl = `${this.bikologiWheelImageUrl}?${new URLSearchParams(imageUrlParams).toString()}`;
            console.log('[BIKOLOGI] Bikologi Wheel Image Url: ', bikologiImageUrl);
            this.canvas.setAttribute('data-src', bikologiImageUrl);
        } else if (this.builderType === 'bike') {
            let bikologiImageUrl = `${this.bikologiBikeImageUrl}?${new URLSearchParams(imageUrlParams).toString()}`;
            console.log('[BIKOLOGI] Bikologi Bike Image Url: ', bikologiImageUrl);
            this.canvas.setAttribute('data-src', bikologiImageUrl);
        } else {
            console.error('Incorrect Builder Type in constructor');
        }
    }

    // Builds URL parameters for image
    buildImageUrlParams() {
        // console.log('----------------------------------------------------------');
        // console.log('[BIKOLOGI] buildImageUrlParams() START');

        const imageUrlParams = {};

        const components = {
            frame: this.findComponent('frame'),
            shock: this.findComponent('rear-shocks'),
            fork: this.findComponent('fork'),
            headset: this.findComponent('headset'),
            crankset: this.findComponent('crankset'),
            bb: this.findComponent('bottom-brackets'),
            chainring: this.findComponent('chainrings'),
            rearDerailleur: this.findComponent('rear-derailleur'),
            cassette: this.findComponent('cassette'),
            chain: this.findComponent('chain'),
            chainGuide: this.findComponent('chainguide'),
            wheelset: this.findComponent('factory-wheelset'),
            frontTire: this.findComponent('front-tires'),
            rearTire: this.findComponent('rear-tires'),
            stem: this.findComponent('stem'),
            handlebar: this.findComponent('handlebar'),
            seatpost: this.findComponent('seatpost'),
            saddle: this.findComponent('saddle'),
            frontBrake: this.findComponent('front-brake'),
            rearBrake: this.findComponent('rear-brake'),
            rearRim: this.findComponent('rear-rim'),
            frontRim: this.findComponent('front-rim'),
            rearHub: this.findComponent('rear-hub'),
            frontHub: this.findComponent('front-hub'),
            spokes: this.findComponent('spokes'),
            nipples: this.findComponent('spoke-nipples'),
            frontRotor: this.findComponent('front-rotor'),
            rearRotor: this.findComponent('rear-rotor'),
            pedals: this.findComponent('pedals'),
            frameDecals: this.findComponent('frame-decals'),
            valveStems: this.findComponent('valve-stems'),
            crankDecals: this.findComponent('crank-decals'),
            forkDecals: this.findComponent('fork-decals'),
            shockDecals: this.findComponent('rear-shock-decals'),
            rimDecals: this.findComponent('rim-decals')
        };

        // FRAME & DECALS
        if (components.frame) {
            const binding = this.getBinding(components.frame);
            if (binding) {
                imageUrlParams.bikeID = binding.bikologiId;
                imageUrlParams.bikeColor = binding.bikologiColor;
            } else {
                console.warn('[BIKOLOGI] No binding found for frame component:', components.frame);
            }
        }
        if (components.frameDecals) {
            imageUrlParams.bikeDecalColor = components.frameDecals.color;
        }

        // SHOCK & DECALS
        if (components.shock) {
            const binding = this.getBinding(components.shock);
            if (binding) {
                imageUrlParams.shockID = binding.bikologiId;
                imageUrlParams.shockColor = binding.bikologiColor;
            } else {
                console.warn('[BIKOLOGI] No binding found for shock component:', components.shock);
            }
        }
        if (components.shockDecals) {
            imageUrlParams.shockDecalColor = components.shockDecals.color;
        }

        // FORK & DECALS
        if (components.fork) {
            const binding = this.getBinding(components.fork);
            if (binding) {
                imageUrlParams.forkID = binding.bikologiId;
                imageUrlParams.forkColor = binding.bikologiColor;
            } else {
                console.warn('[BIKOLOGI] No binding found for fork component:', components.fork);
            }
        }
        if (components.forkDecals) {
            imageUrlParams.forkDecalColor = components.forkDecals.color;
        }

        // HEADSET
        if (components.headset) {
            const binding = this.getBinding(components.headset);
            if (binding) {
                imageUrlParams.headsetID = binding.bikologiId;
                imageUrlParams.headsetColor = binding.bikologiColor;
            } else {
                console.warn('[BIKOLOGI] No binding found for headset component:', components.headset);
            }
        }

        // CRANKSET & DECALS
        if (components.crankset) {
            const binding = this.getBinding(components.crankset);
            if (binding) {
                imageUrlParams.cranksetID = binding.bikologiId;
                imageUrlParams.cranksetColor = binding.bikologiColor;
            } else {
                console.warn('[BIKOLOGI] No binding found for crankset component:', components.crankset);
            }
        }
        if (components.crankDecals) {
            imageUrlParams.cranksetDecalColor = components.crankDecals.color;
        }

        // BOTTOM BRACKET
        if (components.bb) {
            const binding = this.getBinding(components.bb);
            if (binding) {
                imageUrlParams.bottomBracketID = binding.bikologiId;
                imageUrlParams.bottomBracketColor = binding.bikologiColor;
            } else {
                console.warn('[BIKOLOGI] No binding found for bottom bracket component:', components.bb);
            }
        }

        // CHAINRING
        if (components.chainring) {
            const binding = this.getBinding(components.chainring);
            if (binding) {
                imageUrlParams.chainRingID = binding.bikologiId;
                imageUrlParams.chainRingColor = binding.bikologiColor;
            } else {
                console.warn('[BIKOLOGI] No binding found for chainring component:', components.chainring);
            }
        }

        // REAR DERAILLEUR
        if (components.rearDerailleur) {
            const binding = this.getBinding(components.rearDerailleur);
            if (binding) {
                imageUrlParams.rearDerailleurID = binding.bikologiId;
                imageUrlParams.rearDerailleurColor = binding.bikologiColor;
            } else {
                console.warn('[BIKOLOGI] No binding found for rear derailleur component:', components.rearDerailleur);
            }
        }

        // CASSETTE
        if (components.cassette) {
            const binding = this.getBinding(components.cassette);
            if (binding) {
                imageUrlParams.cassetteID = binding.bikologiId;
                imageUrlParams.cassetteColor = binding.bikologiColor;
            } else {
                console.warn('[BIKOLOGI] No binding found for cassette component:', components.cassette);
            }
        }

        // CHAIN
        if (components.chain) {
            const binding = this.getBinding(components.chain);
            if (binding) {
                imageUrlParams.chainID = binding.bikologiId;
                imageUrlParams.chainColor = binding.bikologiColor;
            } else {
                console.warn('[BIKOLOGI] No binding found for chain component:', components.chain);
            }
        }

        // CHAIN GUIDE
        if (components.chainGuide) {
            const binding = this.getBinding(components.chainGuide);
            if (binding) {
                imageUrlParams.chainGuideID = binding.bikologiId;
                imageUrlParams.chainGuideColor = binding.bikologiColor;
            } else {
                console.warn('[BIKOLOGI] No binding found for chainguide component:', components.chainGuide);
            }
        }

        // WHEELSET & DECALS
        if (components.wheelset) {
            const binding = this.getBinding(components.wheelset);
            if (binding) {
                imageUrlParams.wheelsID = binding.bikologiId;
                imageUrlParams.wheelsColor = binding.bikologiColor;
                if (components.rimDecals) {
                    imageUrlParams.wheelsDecalColor = components.rimDecals.color;
                }
            } else {
                console.warn('[BIKOLOGI] No binding found for wheelset component:', components.wheelset);
            }
        }

        // FRONT TIRE
        if (components.frontTire) {
            const binding = this.getBinding(components.frontTire);
            if (binding) {
                imageUrlParams.frontTireID = binding.bikologiId;
                imageUrlParams.frontTireColor = binding.bikologiColor;
            } else {
                console.warn('[BIKOLOGI] No binding found for front tire component:', components.frontTire);
            }
        }

        // REAR TIRE
        if (components.rearTire) {
            const binding = this.getBinding(components.rearTire);
            if (binding) {
                imageUrlParams.rearTireID = binding.bikologiId;
                imageUrlParams.rearTireColor = binding.bikologiColor;
            } else {
                console.warn('[BIKOLOGI] No binding found for rear tire component:', components.rearTire);
            }
        }

        // STEM
        if (components.stem) {
            const binding = this.getBinding(components.stem);
            if (binding) {
                imageUrlParams.stemID = binding.bikologiId;
                imageUrlParams.stemColor = binding.bikologiColor;
            } else {
                console.warn('[BIKOLOGI] No binding found for stem component:', components.stem);
            }
        }

        // HANDLEBAR
        if (components.handlebar) {
            const binding = this.getBinding(components.handlebar);
            if (binding) {
                imageUrlParams.handlebarID = binding.bikologiId;
                imageUrlParams.handlebarColor = binding.bikologiColor;
            } else {
                console.warn('[BIKOLOGI] No binding found for handlebar component:', components.handlebar);
            }
        }

        // SEATPOST
        if (components.seatpost) {
            const binding = this.getBinding(components.seatpost);
            if (binding) {
                imageUrlParams.seatPostID = binding.bikologiId;
                imageUrlParams.seatPostColor = binding.bikologiColor;
            } else {
                console.warn('[BIKOLOGI] No binding found for seatpost component:', components.seatpost);
            }
        }

        // SADDLE
        if (components.saddle) {
            const binding = this.getBinding(components.saddle);
            if (binding) {
                imageUrlParams.saddleID = binding.bikologiId;
                imageUrlParams.saddleColor = binding.bikologiColor;
            } else {
                console.warn('[BIKOLOGI] No binding found for saddle component:', components.saddle);
            }
        }

        // PEDALS
        if (components.pedals) {
            const binding = this.getBinding(components.pedals);
            if (binding) {
                imageUrlParams.pedalID = binding.bikologiId;
                imageUrlParams.pedalColor = binding.bikologiColor;
            } else {
                console.warn('[BIKOLOGI] No binding found for pedal component:', components.pedals);
            }
        }

        // FRONT BRAKE
        if (components.frontBrake) {
            const binding = this.getBinding(components.frontBrake);
            if (binding) {
                imageUrlParams.frontBrakeID = binding.bikologiId;
                imageUrlParams.frontBrakeColor = binding.bikologiColor;
            } else {
                console.warn('[BIKOLOGI] No binding found for front brake component:', components.frontBrake);
            }
        }

        // REAR BRAKE
        if (components.rearBrake) {
            const binding = this.getBinding(components.rearBrake);
            if (binding) {
                imageUrlParams.rearBrakeID = binding.bikologiId;
                imageUrlParams.rearBrakeColor = binding.bikologiColor;
            } else {
                console.warn('[BIKOLOGI] No binding found for rear brake component:', components.rearBrake);
            }
        }

        // FRONT ROTOR
        if (components.frontRotor) {
            const binding = this.getBinding(components.frontRotor);
            if (binding) {
                imageUrlParams.frontRotorID = binding.bikologiId;
                imageUrlParams.frontRotorColor = binding.bikologiColor;
            } else {
                console.warn('[BIKOLOGI] No binding found for front rotor component:', components.frontRotor);
            }
        }

        // REAR ROTOR
        if (components.rearRotor) {
            const binding = this.getBinding(components.rearRotor);
            if (binding) {
                imageUrlParams.rearRotorID = binding.bikologiId;
                imageUrlParams.rearRotorColor = binding.bikologiColor;
            } else {
                console.warn('[BIKOLOGI] No binding found for rear rotor component:', components.rearRotor);
            }
        }

        // FRONT RIM
        if (components.frontRim) {
            const binding = this.getBinding(components.frontRim);
            if (binding) {
                imageUrlParams.frontRimID = binding.bikologiId;
                imageUrlParams.frontRimColor = binding.bikologiColor;
            } else {
                console.warn('[BIKOLOGI] No binding found for front rim component:', components.frontRim);
            }
        }

        // REAR RIM
        if (components.rearRim) {
            const binding = this.getBinding(components.rearRim);
            if (binding) {
                imageUrlParams.rearRimID = binding.bikologiId;
                imageUrlParams.rearRimColor = binding.bikologiColor;
            } else {
                console.warn('[BIKOLOGI] No binding found for rear rim component:', components.rearRim);
            }
        }

        // Apply Rim Decals if rims exist
        if (components.frontRim || components.rearRim) {
            if (components.rimDecals) {
                imageUrlParams.frontRimDecalColor = components.rimDecals.color;
                imageUrlParams.rearRimDecalColor = components.rimDecals.color;
            }
        }

        // FRONT HUB
        if (components.frontHub) {
            const binding = this.getBinding(components.frontHub);
            if (binding) {
                imageUrlParams.frontHubID = binding.bikologiId;
                imageUrlParams.frontHubColor = binding.bikologiColor;
            } else {
                console.warn('[BIKOLOGI] No binding found for front hub component:', components.frontHub);
            }
        }

        // REAR HUB
        if (components.rearHub) {
            const binding = this.getBinding(components.rearHub);
            if (binding) {
                imageUrlParams.rearHubID = binding.bikologiId;
                imageUrlParams.rearHubColor = binding.bikologiColor;
            } else {
                console.warn('[BIKOLOGI] No binding found for rear hub component:', components.rearHub);
            }
        }

        // VALVE STEMS
        if (components.valveStems) {
            const binding = this.getBinding(components.valveStems);
            const selections = this.selections;
            if (binding) {
                if (selections[1]?.value === 'wheelset' || selections[1]?.handle === 'frame') {
                    imageUrlParams.frontValveStemID = binding.bikologiId;
                    imageUrlParams.frontValveStemColor = binding.bikologiColor;
                    imageUrlParams.rearValveStemID = binding.bikologiId;
                    imageUrlParams.rearValveStemColor = binding.bikologiColor;
                } else if (selections[1]?.value === 'rear-wheel') {
                    imageUrlParams.rearValveStemID = binding.bikologiId;
                    imageUrlParams.rearValveStemColor = binding.bikologiColor;
                } else if (selections[1]?.value === 'front-wheel') {
                    imageUrlParams.frontValveStemID = binding.bikologiId;
                    imageUrlParams.frontValveStemColor = binding.bikologiColor;
                }
            } else {
                console.warn('[BIKOLOGI] No binding found for valve stem component:', components.valveStems);
            }


        }

        // SPOKES & CUSTOM SPOKE PATTERN
        if (components.spokes) {
            const binding = this.getBinding(components.spokes);
            if (binding) {
                if (this.customPickerInit === true && window.location.pathname.includes('wheel-builder')) {
                    imageUrlParams.customSpokesID = '5e24c5d9eccb66000484ae53'; // hardcoded ID for custom spokes
                    imageUrlParams.customSpokesColor = binding.bikologiColor;

                    for (const key in this.spokePattern) {
                        imageUrlParams[key] = this.spokePattern[key];
                    }

                } else if (this.selections[1]?.value === 'front-wheel') {
                    imageUrlParams.frontSpokeID = binding.bikologiId;
                    imageUrlParams.frontSpokeColor = binding.bikologiColor;
                } else if (this.selections[1]?.value === 'rear-wheel') {
                    imageUrlParams.rearSpokeID = binding.bikologiId;
                    imageUrlParams.rearSpokeColor = binding.bikologiColor;
                } else {
                    imageUrlParams.frontSpokeID = binding.bikologiId;
                    imageUrlParams.frontSpokeColor = binding.bikologiColor;
                    imageUrlParams.rearSpokeID = binding.bikologiId;
                    imageUrlParams.rearSpokeColor = binding.bikologiColor;
                }
            } else {
                console.warn('BIKOLOGI - No binding found for spokes component:', components.spokes);
            }
        }

        // NIPPLES (same color logic as spokes)
        if (components.nipples) {
            const binding = this.getBinding(components.nipples);

            if (binding) {
                if (this.selections[1]?.value === 'front-wheel') {
                    imageUrlParams.frontNippleID = binding.bikologiId;
                    imageUrlParams.frontNippleColor = binding.bikologiColor;
                } else if (this.selections[1]?.value === 'rear-wheel') {
                    imageUrlParams.rearNippleID = binding.bikologiId;
                    imageUrlParams.rearNippleColor = binding.bikologiColor;
                } else {
                    imageUrlParams.frontNippleID = binding.bikologiId;
                    imageUrlParams.frontNippleColor = binding.bikologiColor;
                    imageUrlParams.rearNippleID = binding.bikologiId;
                    imageUrlParams.rearNippleColor = binding.bikologiColor;
                }
            } else {
                console.warn('BIKOLOGI - No binding found for nipples component:', components.nipples);
            }
        }

        // Add URL param for wheel or wheelset
        if (this.builderType === 'wheel') {
            if (this.selections[1].value === 'wheelset') {
                imageUrlParams.wheelsType = 'wheelset';
            } else {
                imageUrlParams.wheelsType = 'wheel';
            }
        }

        return imageUrlParams;

    }

    // Parses bike and returns components with their Bikologi URL
    parseBike(query) {
        console.log('----------------------------------------------------------');
        console.log('[BIKOLOGI] Running parseBike() with query:', query);

        const bikeInfo = {
            reference: {}
        };

        const bike = this.bikes[query.bikeID];
        console.log('[BIKOLOGI] Fetched bike object:', bike);

        if (!bike || !bike.images) {
            console.error('[BIKOLOGI] parseBike() - Bike not found or missing images for ID:', query.bikeID);
            return bikeInfo;
        }

        // Set reference to master bike
        bikeInfo.reference.bike = bike;

        // Default background override
        if (query.background === 'fanatik') {
            bikeInfo.background = 'http://content.bikologi.com/backgrounds/fanatik.jpg';
        }

        // Loop over all components in the query
        for (const componentID in query) {
            const param = componentID.replace('ID', '');
            const isComponent = componentID.includes('ID') && componentID !== 'bikeID';
            const component = isComponent ? this.components[query[componentID]] : null;

            console.log('ComponentID:', componentID);
            console.log('Param:', param);
            console.log('Component: ', component);

            if (!component || !component.images || !component.images.builder) continue;

            const builderImages = component.images.builder;
            const usage = component.usage || [];

            let defaultImage = '',
                defaultFork = '',
                defaultForkLowers = '',
                defaultCrankset = '',
                defaultShock = '',
                defaultFrontRim = '',
                defaultRearRim = '',
                defaultWheels = '',
                color = query[param + 'Color'] || '';

            for (const img of builderImages) {
                console.log('Img:', img);

                img.color = img.color?.toLowerCase();

                switch (param) {
                    case 'fork': {
                        console.log('We have a fork');
                        const isDownhill = usage.includes('downhill');
                        console.log('isDownhill:', isDownhill);
                        if (!query.forkColor && img.position !== 'steerer') {
                            console.log('No fork color or steerer. img.color:', img.color);
                            query.forkColor = img.color;
                        }

                        if (img.color === query.forkColor) {
                            console.log('img.color equals query.forkColor');
                            if (img.position === 'top') bikeInfo.forkCrown = img.url;
                            if (img.position === 'bottom' && !query.forkDecalColor) bikeInfo.forkLowers = img.url;
                            if (img.position === 'bottom') defaultForkLowers = img.url;
                            if (img.position === 'brake-mount') bikeInfo.forkBrakeMount = img.url;
                            if (!img.position && !query.forkDecalColor) bikeInfo.fork = img.url;
                            if (!img.position) defaultFork = img.url;
                            if (img.position === 'steerer') bikeInfo.forkSteerer = img.url;

                            if (query.forkDecalColor && !bikeInfo.forkLowers && !bikeInfo.fork) {
                                if (isDownhill) {
                                    bikeInfo.fork = this.compareDecal(query.forkDecalColor, defaultFork, img.color, builderImages, true);
                                } else {
                                    console.log('Calling compareDecal from parseBike()');
                                    bikeInfo.forkLowers = this.compareDecal(query.forkDecalColor, defaultForkLowers, img.color, builderImages, false);
                                }
                            }
                        }

                        // Fallbacks
                        if (!bikeInfo.forkBrakeMount) {
                            const fallbackColors = {
                                black: 'http://content.bikologi.com/component/2018/fox/fork/float-evol-hsc-lsc-black-brake-mount-desktop.png',
                                orange: 'http://content.bikologi.com/component/2018/fox/fork/float-evol-hsc-lsc-orange-brake-mount-desktop.png',
                                red: 'http://content.bikologi.com/component/2019/rock-shox/fork/lyrik-rc2-red-brake-mount-desktop.png'
                            };
                            const fallback = fallbackColors[query.forkColor?.toLowerCase()];
                            if (fallback) bikeInfo.forkBrakeMount = fallback;
                        }

                        if (!bikeInfo.forkSteerer) {
                            bikeInfo.forkSteerer = 'http://content.bikologi.com/component/2018/fox/fork/float-evol-hsc-lsc-black-steerer-desktop.png';
                        }

                        break;
                    }

                    case 'crankset': {
                        if (!query[param + 'Color']) {
                            query[param + 'Color'] = img.color;
                        }

                        const matchColor = img.color === query[param + 'Color'];
                        const hasNoDecal = !img.decal;

                        if (matchColor && hasNoDecal) {
                            if (!['top', 'bottom'].includes(img.position)) defaultCrankset = img.url;
                            if (img.position === 'top') bikeInfo.crankTop = img.url;
                            if (img.position === 'bottom') bikeInfo.crankBottom = img.url;
                            if (!img.position) bikeInfo.crankset = img.url;
                        }

                        if (query.cranksetDecalColor && !bikeInfo.crankset) {
                            bikeInfo.crankset = this.compareDecal(query.cranksetDecalColor, defaultCrankset, img.color, builderImages);
                        }

                        break;
                    }

                    case 'chain': {
                        if (!query.chainColor) {
                            query.chainColor = img.color;
                        }

                        if (img.color === query.chainColor) {
                            const pos = img.position;

                            if (bike.topRoller) {
                                if (pos === 'chain-top') bikeInfo.chainTop = img.url;
                                if (pos === 'chain-bottom') bikeInfo.chainBottom = img.url;
                                if (pos === 'top-chain-ring') bikeInfo.chainOnRing = img.url;
                                if (pos === 'chain-bottom-roller') bikeInfo.chainBottomRoller = img.url;
                                if (pos === 'top-chain-ring-roller') bikeInfo.chainRingRoller = img.url;
                                if (pos === 'top-chain-ring-oval') bikeInfo.chainRingOval = img.url;
                                if (pos === 'top-chain-ring-oval-roller') bikeInfo.chainRingOvalRoller = img.url;
                                if (pos === 'top-roller') bikeInfo.topRoller = img.url;
                            } else {
                                if (pos === 'chain-top') bikeInfo.chainTop = img.url;
                                if (pos === 'chain-bottom') bikeInfo.chainBottom = img.url;
                                if (pos === 'chain-on-ring') bikeInfo.chainOnRing = img.url;
                                if (pos === 'chain-bottom-roller') bikeInfo.chainBottomRoller = img.url;
                                if (pos === 'chain-ring-roller') bikeInfo.chainRingRoller = img.url;
                                if (pos === 'chain-ring-oval') bikeInfo.chainRingOval = img.url;
                                if (pos === 'chain-ring-oval-roller') bikeInfo.chainRingOvalRoller = img.url;
                            }
                        }

                        bikeInfo.reference['chain'] = component;
                        break;
                    }

                    case 'shock': {
                        const useFlipped = bike.shockFlipped && img.flipped;
                        const useNormal = !bike.shockFlipped && !img.flipped;

                        if (useFlipped || useNormal) {
                            if (!query.shockColor) query.shockColor = img.color;

                            if (img.color === query.shockColor && !query.shockDecalColor && !img.decal) {
                                bikeInfo.shock = img.url;
                            }

                            if (img.color === query.shockColor && !img.decal) {
                                defaultShock = img.url;
                            }

                            if (query.shockDecalColor && !bikeInfo.shock) {
                                bikeInfo.shock = this.compareDecal(query.shockDecalColor, defaultShock, img.color, builderImages, bike.shockFlipped);
                            }
                        }

                        break;
                    }

                    case 'frame': {
                        if (!query.bikeColor && img.color && !img.position) {
                            query.bikeColor = img.color;
                        }

                        if (img.color === query.bikeColor) {
                            if (!img.position && !query.bikeDecalColor) bikeInfo.frame = img.url;
                            if (!img.position) defaultImage = img.url;
                            if (img.position === 'brake-mount') bikeInfo.frameBrakeMount = img.url;
                            if (img.position === 'stays') bikeInfo.frameStays = img.url;
                            if (img.position === 'detail') bikeInfo.frameDetail = img.url;
                            if (img.position === 'dh-cables') bikeInfo.dhFrameDetail = img.url;
                        }

                        if (query.bikeDecalColor && !bikeInfo.frame) {
                            bikeInfo.frame = this.compareDecal(query.bikeDecalColor, defaultImage, query.bikeColor, bike.images.builder);
                        }

                        bikeInfo.reference[param] = bike;
                        break;
                    }

                    case 'frontRim': {
                        if (!query.frontRimColor) {
                            query.frontRimColor = img.color;
                        }

                        if (!query.frontRimDecalColor && !img.decal && img.color === query.frontRimColor) {
                            bikeInfo.frontRim = img.url;
                        }

                        if (img.color === query.frontRimColor) {
                            defaultFrontRim = img.url;
                        }

                        if (query.frontRimDecalColor && !bikeInfo.frontRim) {
                            bikeInfo.frontRim = this.compareDecal(query.frontRimDecalColor, defaultFrontRim, img.color, builderImages, false)
                        }

                        break;
                    }

                    case 'rearRim': {
                        if (!query.rearRimColor) {
                            query.rearRimColor = img.color;
                        }

                        if (!query.rearRimDecalColor && !img.decal && img.color === query.rearRimColor) {
                            bikeInfo.rearRim = img.url;
                        }

                        if (img.color === query.rearRimColor) {
                            defaultRearRim = img.url;
                        }

                        if (query.rearRimDecalColor && !bikeInfo.rearRim) {
                            bikeInfo.rearRim = this.compareDecal(query.rearRimDecalColor, defaultRearRim, img.color, builderImages, false)
                        }

                        break;
                    }

                    case 'wheels': {
                        console.log('We have wheels');
                        if (!query.wheelsColor) {
                            console.log('No wheels color in query');
                            query.wheelsColor = img.color;
                        }

                        if (!query.wheelsDecalColor && !img.decal && img.color === query.wheelsColor) {
                            console.log('No decals and wheels color matches image color');
                            bikeInfo.wheels = img.url;
                        }

                        if (img.color === query.wheelsColor) {
                            console.log('Setup defaultWheels to current image');
                            defaultWheels = img.url;
                        }

                        if (query.wheelsDecalColor && !bikeInfo.wheels) {
                            console.log('Query has wheelsDecalColor but bikeInfo.wheels not set yet, run compare decal!');
                            bikeInfo.wheels = this.compareDecal(query.wheelsDecalColor, defaultWheels, img.color, builderImages, false)
                        }

                        break;
                    }

                    default: {
                        const paramColor = query[param + 'Color'] || img.color;
                        const colorMatch = img.color === paramColor;

                        if (colorMatch && !img.position) {
                            bikeInfo[param] = img.url;
                        }

                        break;
                    }
                }
            }

            bikeInfo.reference[param] = component;
        }

        // Chain derailleur overlay
        if (query.chainID && query.rearDerailleurID && bikeInfo.reference.rearDerailleur) {
            for (const img of bikeInfo.reference.rearDerailleur.images.builder) {
                if (img.position === 'der-color' && img.color === query.chainColor) {
                    bikeInfo.chainDerailleur = img.url;
                    break;
                }
            }
        }

        // Select a default frame color if not provided
        if (!query.bikeColor) {
            for (let i = 0; i < bike.images.builder.length; i++) {
                let img = bike.images.builder[i];

                if (!query.bikeColor && !img.position && img.color !== undefined) {
                    query.bikeColor = bike.images.builder[i].color;
                }
            }
        }

        // Bind bike images to the right places
        for (let i = 0; i < bike.images.builder.length; i++) {
            let img = bike.images.builder[i],
                defaultFrame = '';

            if (img.color.toLowerCase() === query.bikeColor) {
                if (!img.position && !query.bikeDecalColor) {
                    bikeInfo.frame = img.url;
                }

                if (!img.position) {
                    defaultFrame = img.url;
                }

                if (img.position === 'brake-mount') {
                    bikeInfo.frameBrakeMount = img.url;
                }

                if (img.position === 'stays') {
                    bikeInfo.frameStays = img.url;
                }

                if (query.bikeDecalColor && !bikeInfo.frame) {
                    bikeInfo.frame = this.compareDecal(query.bikeDecalColor, defaultFrame, img.color, bike.images.builder);
                }
            }

            if (img.position === 'chain-on-ring') {
                bikeInfo['chain-' + img.color] = img.url;
            }

            if (img.position === 'detail') {
                bikeInfo.frameDetail = img.url;
            }

            if (img.position === 'dh-cables') {
                bikeInfo.dhFrameDetail = img.url;
            }
        }

        console.log('[BIKOLOGI] parseBike() - Final bikeInfo:', bikeInfo);
        return bikeInfo;
    }

    // Compares decal
    compareDecal(queryDecal, defaultImage, color, images, flipped = undefined) {
        console.log('----------------------------------------------------------');
        console.log('Running compareDecal()');
        console.log('queryDecal:', queryDecal);
        console.log('defaultImage:', defaultImage);
        console.log('color', color);
        console.log('images', images);

        if (!queryDecal || !images || images.length === 0) {
            console.warn('compareDecal(): No decal query or images to compare.');
            return defaultImage;
        }

        const order = images.filter(img => {
            const colorMatch = img.color?.toLowerCase() === color.toLowerCase();
            let flippedMatch = true;
            if (flipped === true) {
                flippedMatch = img.flipped === true;
            } else if (flipped === false) {
                flippedMatch = img.flipped !== true;
            }
            return img.decal && colorMatch && flippedMatch;
        }).map(img => {
            img.similarity = CustomBuilder.similarity(img.decal, queryDecal);
            return img;
        });

        if (order.length === 0) {
            console.warn('compareDecal(): No matching images found for color:', color);
            return defaultImage;
        }

        // Sort best matches to the top
        order.sort((a, b) => b.similarity - a.similarity);

        const bestMatch = order[0];
        console.log(`compareDecal(): Best match similarity ${bestMatch.similarity}`, bestMatch);

        return bestMatch.similarity > 0.5 ? bestMatch.url : defaultImage;
    }


    // WHEEL BUILDER CODE



    renderWheelImages(wheel, query) {
        console.log('[BIKOLOG] Entered renderWheelImages()');
    }

    parseWheel(query) {
        console.log('----------------------------------------------------------');
        console.log('[BIKOLOGI] Running parseWheel() with query:', query);

        const wheelInfo = {
            reference: {}
        };

        // Loop over all components in the query
        for (const componentID in query) {
            const param = componentID.replace('ID', '');
            const isComponent = componentID.includes('ID') && componentID !== 'bikeID';
            const component = isComponent ? this.components[query[componentID]] : null;

            console.log('ComponentID:', componentID);
            console.log('Param:', param);
            console.log('Component: ', component);

            if (!component || !component.images || !component.images.builder) continue;

            const builderImages = component.images.builder;
            const usage = component.usage || [];

            let defaultImage = '',
                defaultFrontRim = '',
                defaultRearRim = '';

            for (const img of builderImages) {
                console.log('Img:', img);

                img.color = img.color?.toLowerCase();

                switch (param) {

                    case 'frontRim': {
                        if (!query.frontRimColor) {
                            query.frontRimColor = img.color;
                        }

                        if (!query.frontRimDecalColor && !img.decal && img.color === query.frontRimColor) {
                            wheelInfo.frontRim = img.url;
                        }

                        if (img.color === query.frontRimColor) {
                            defaultFrontRim = img.url;
                        }

                        if (query.frontRimDecalColor && !wheelInfo.frontRim) {
                            wheelInfo.frontRim = this.compareDecal(query.frontRimDecalColor, defaultFrontRim, img.color, builderImages, false)
                        }

                        break;
                    }

                    case 'rearRim': {
                        if (!query.rearRimColor) {
                            query.rearRimColor = img.color;
                        }

                        if (!query.rearRimDecalColor && !img.decal && img.color === query.rearRimColor) {
                            wheelInfo.rearRim = img.url;
                        }

                        if (img.color === query.rearRimColor) {
                            defaultRearRim = img.url;
                        }

                        if (query.rearRimDecalColor && !wheelInfo.rearRim) {
                            wheelInfo.rearRim = this.compareDecal(query.rearRimDecalColor, defaultRearRim, img.color, builderImages, false)
                        }

                        break;
                    }

                    default: {
                        const paramColor = query[param + 'Color'] || img.color;
                        const colorMatch = img.color === paramColor;

                        if (colorMatch && !img.position) {
                            wheelInfo[param] = img.url;
                        }

                        break;
                    }
                }
            }

            wheelInfo.reference[param] = component;
        }

        console.log('[BIKOLOGI] parseWheel() - Final bikeInfo:', wheelInfo);
        return wheelInfo;
    }


    // Not sure what this does
    static levenshteinDistance(s1, s2) {
        const len1 = s1.length;
        const len2 = s2.length;

        const matrix = Array.from({ length: len1 + 1 }, () => Array(len2 + 1).fill(0));

        for (let i = 0; i <= len1; i++) matrix[i][0] = i;
        for (let j = 0; j <= len2; j++) matrix[0][j] = j;

        for (let i = 1; i <= len1; i++) {
            for (let j = 1; j <= len2; j++) {
                if (s1[i - 1] === s2[j - 1]) {
                    matrix[i][j] = matrix[i - 1][j - 1];
                } else {
                    matrix[i][j] = Math.min(
                        matrix[i - 1][j] + 1,
                        matrix[i][j - 1] + 1,
                        matrix[i - 1][j - 1] + 1
                    );
                }
            }
        }

        return matrix[len1][len2];
    }

    // Not sure what this does
    static similarity(s1, s2) {
        if (!s1.length && !s2.length) return 1.0;

        const longer = s1.length > s2.length ? s1 : s2;
        const shorter = s1.length > s2.length ? s2 : s1;
        const longerLength = longer.length;

        if (longerLength === 0) return 1.0;

        const distance = CustomBuilder.levenshteinDistance(longer.toLowerCase(), shorter.toLowerCase());

        return (longerLength - distance) / longerLength;
    }

    static handleize(str) {
        return str
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '-')            // Replace spaces with -
            .replace(/[^a-z0-9\-]/g, '-')    // Remove all non-alphanumeric and non-dash characters
            .replace(/\-+/g, '-');           // Replace multiple dashes with a single dash
    }

}
